import React, { useContext, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import VariableContext from "../contexts/VariableContext";
import FetchingFreq from "../contexts/FetchingFreq";
import apiService from "../services/apiService";
import { useToast } from "../contexts/ToastContext";
import { useConfirm } from "../contexts/ConfirmContext";
import OpenProperties from "./OpenProperties";
import MyChartComponent from "./MyChartComponent";
import FrequencyTableComponent from "./FrequencyTableComponent";
import HoverDropdown from "./HoverDropdown";
import MultiResponse from "./MultiResponse";
import DataGrid from "./DataGrid";
import MultiDataGrid from "./MultiDataGrid";
import Grid3DData from "./Grid3DData";
import CrosstabTable from "./CrosstabTable";
import CustomizedVariable from "./CustomizedVariable";
import PropertiesComponent from "./PropertiesComponent";
import DeleteIdsModal from "./DeleteIdsModal";
import OeImportModal from "./OeImportModal";
import ExcelVariableUploadModal from "./ExcelVariableUploadModal";
import MoveVariableToGridModal from "./MoveVariableToGridModal";

import * as XLSX from "xlsx";
import { useAuth } from "../providers/AuthProvider";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./styles/DataTables.css";

import {
  faFolder,
  faFile,
  faTable,
  faChartBar,
  faArrowLeft,
  faGear,
  faDownload,
  faGauge,
  faBuilding,
  faWeightScale,
  faPeopleRoof,
  faHouse,
  faLaptopFile,
  faChargingStation,
  faUpload,
  faPlus,
  faGlobe,
  faFileExport,
  faArrowsLeftRight,
  faClockRotateLeft,
  faRefresh,
  faRobot,
  faTimes,
  faCheckCircle,
  faSpinner,
  faTrash,
  faFileExcel,
  faFileAlt,
  faPaperPlane,
  faComments,
  faSave,
  faList,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import Loader from "./Loader";
import RunTableSettingsModal from "./RunTableSettingsModal";
import RunMatrixModal from "./RunMatrixModal";
import DataGroupVersionsModal from "./DataGroupVersionsModal";

function DataTables() {
  const {
    dataGroup,
    setdataGroup,
    selectedVariables,
    banners,
    setBanners,
    setSelectedVariables,
    projectId,
    savedWeightings,
    saveCurrentWeighting,
    currentSelectedVariablesset,
    setcurrentSelectedVariablesset,
    setSavedWeightings,
    weightingStats,
    setWeightingStats,

    currentSelectedVariables,
    setCurrentSelectedVariables,
    setSelectedClient,
    selectedProject,
    selectedClient,
    powner,
    setSdataGroup
  } = useContext(VariableContext);

  const {
    setMessage,
    setActivePage,
    handleTitleClick,
    selectedTitle,
    setSelectedTitle,
    refreshData,
    setRefreshData,
    selectedBanner,
    setselectedBanner,
    selectedWeights,
    setSelectedWeights,
    selectedVariable,
    setSelectedVariable,
    loading,
    setLoading, isPropertiesShow, setIsPropertiesShow, nets, setNets,
    frequencyData,
    overlapCorrection, setOverlapCorrection,
  } = useContext(FetchingFreq);

  const { user } = useAuth();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAModalOpen, setIsaModalOpen] = useState(false);
  const [isDeleteIdsOpen, setIsDeleteIdsOpen] = useState(false);
  const [isOeImportOpen, setIsOeImportOpen] = useState(false);
  const [isExcelVarUploadOpen, setIsExcelVarUploadOpen] = useState(false);
  const [vtmDownloading, setVtmDownloading] = useState(false);
  const [isMoveVarOpen, setIsMoveVarOpen] = useState(false);
  const [isbilendi, setBilendi] = useState(false);
  const [bilendiSeparateSheet, setBilendiSeparateSheet] = useState(false);
  const [saveBanner, setSaveBanner] = useState(false);

  const [jsonScript, setJsonScript] = useState(false);

  // ── Shared import file ref ────────────────────────────────────────────────
  const sharedFileRef = useRef(null);
  const [sharedFile, setSharedFile] = useState(null);
  const [sharedFileAccept, setSharedFileAccept] = useState("*");

  const openSharedPicker = (accept) => {
    setSharedFileAccept(accept);
    // small timeout so accept attr updates before click
    setTimeout(() => { if (sharedFileRef.current) { sharedFileRef.current.accept = accept; sharedFileRef.current.value = ""; sharedFileRef.current.click(); } }, 10);
  };

  const onSharedFileChange = (e) => {
    const file = e.target.files[0] || null;
    setSharedFile(file);
  };

  // Dispatch to Tab Specs modal (Excel)
  const dispatchTabSpecs = async () => {
    if (tabSpecsAlreadyUsed) {
      const ok = await confirm({
        title: "Tab Specs Already Processed",
        message: "Tab specs have already been uploaded and processed for this project. Uploading again may cause duplicate data or excessive token usage. Are you sure you want to continue?",
        confirmText: "Upload Again",
        variant: "warning",
      });
      if (!ok) return;
    }
    toggleTabSpecsModal();
  };

  // ── Helper: read sharedFile as parsed JSON ───────────────────────────────
  const readSharedFileAsJson = () =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try { resolve(JSON.parse(e.target.result)); }
        catch (err) { reject(new Error("Invalid JSON file: " + err.message)); }
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsText(sharedFile);
    });

  // Dispatch to Table JSON → uploads to sdatagroup_json, then updates dataGroup in UI
  const dispatchTableJson = async () => {
    if (!sharedFile) return;
    try {
      setLoading(true);
      // Parse file client-side first so we can update UI immediately
      const parsed = await readSharedFileAsJson();
      // Upload to server
      const formData = new FormData();
      formData.append("file", sharedFile);
      const response = await fetch(
        `${apiService.baseURL}/api/upload-qgroup-json/${projectId}`,
        { method: "POST", body: formData }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || "Upload failed");
      }
      setdataGroup(parsed);
      toast.success("Table JSON uploaded and UI updated successfully");
    } catch (err) {
      toast.error(err.message || "Failed to upload Table JSON");
    } finally {
      setLoading(false);
    }
  };

  // Dispatch to Banner JSON → uploads to banner endpoint, then updates banners in UI
  const dispatchBannerJson = async () => {
    if (!sharedFile) return;
    try {
      setLoading(true);
      const parsed = await readSharedFileAsJson();
      if (!Array.isArray(parsed)) {
        toast.warning("Banner JSON must be an array");
        return;
      }
      const formData = new FormData();
      formData.append("file", sharedFile);
      const response = await fetch(
        `${apiService.baseURL}/api/upload-banner-json/${projectId}`,
        { method: "POST", body: formData }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || "Upload failed");
      }
      setBanners(parsed);
      toast.success("Banner JSON uploaded and UI updated successfully");
    } catch (err) {
      toast.error(err.message || "Failed to upload Banner JSON");
    } finally {
      setLoading(false);
    }
  };

  // Dispatch to Scripting → uploads to qgroup_json, then updates dataGroup in UI
  const dispatchScriptingJson = async () => {
    if (!sharedFile) return;
    try {
      setLoading(true);
      const parsed = await readSharedFileAsJson();
      const formData = new FormData();
      formData.append("file", sharedFile);
      const response = await fetch(
        `${apiService.baseURL}/api/upload-sdatagroup-json/${projectId}`,
        { method: "POST", body: formData }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || "Upload failed");
      }
      setSdataGroup(parsed);
      toast.success("Scripting JSON uploaded and UI updated successfully");
    } catch (err) {
      toast.error(err.message || "Failed to upload Scripting JSON");
    } finally {
      setLoading(false);
    }
  };

  const handleUploadQgroupJson = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setLoading(true);
      // Parse client-side to update UI immediately on success
      const text = await file.text();
      const parsed = JSON.parse(text);
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetch(
        `${apiService.baseURL}/api/upload-qgroup-json/${projectId}`,
        { method: "POST", body: formData }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || "Upload failed");
      }
      setdataGroup(parsed);
      toast.success("JSON uploaded and UI updated successfully");
    } catch (err) {
      toast.error(err.message || "Failed to upload JSON");
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };

  const [exportType, setExportType] = useState("both");
  const [exportOverlay, setExportOverlay] = useState(null); // null | "Quantum" | "Uncle" | "Wincross" | ...
  const [sigText, setsigText] = useState(false);
  const [weightVarInput, setWeightVarInput] = useState("");
  const [runTablesLoading, setRunTablesLoading] = useState(false);
  const [runTablesStatus, setRunTablesStatus] = useState("idle"); // idle | running | done | error
  // 0-100 progress for the background computation, -1 = not running
  const [runTablesProgress, setRunTablesProgress] = useState(-1);
  // 'fast' (in-memory Postgres path) | 'spss_fallback' (slow, single-threaded SPSS read)
  const [runTablesMode, setRunTablesMode] = useState("fast");
  const runTablesJobRef = useRef(null);   // holds { jobId, intervalId } while polling
  // ── Refresh Filter Cache (pre-warms banner_ids_cache for the whole project) ──
  const [refreshCacheStatus, setRefreshCacheStatus] = useState("idle"); // idle | running | done | error
  const [refreshCacheProgress, setRefreshCacheProgress] = useState(-1); // 0-100, -1 = not running
  const [refreshCacheCounts, setRefreshCacheCounts] = useState({ done: 0, total: 0 });
  const refreshCacheJobRef = useRef(null);   // holds { jobId, intervalId } while polling
  const [exportExcelLoading, setExportExcelLoading] = useState(false);
  const [exportExcelStatus, setExportExcelStatus] = useState("idle"); // idle | done | error
  // Number of cached tables in freqdatatable for the current project+banner.
  // -1 = not yet fetched, 0 = empty, >0 = has data.
  const [freqdataCount, setFreqdataCount] = useState(-1);
  const [runTablesSigTest, setRunTablesSigTest] = useState(false);
  const [sigTestWeighted, setSigTestWeighted] = useState(true); // true=weighted, false=unweighted
  const [runTablesLayout, setRunTablesLayout] = useState("rows"); // rows | cols
  const [runTablesSurvation, setRunTablesSurvation] = useState(false);
  const [runTablesToluna, setRunTablesToluna] = useState(false);

  // ── Master table settings (single object — add new keys here as features grow) ──
  const DEFAULT_TABLE_SETTINGS = {
    freqDecimals: 0, pctDecimals: 1, baseDecimals: 0,
    meanDecimals: 2, medianDecimals: 2, stdDecimals: 2, seDecimals: 2,
    modeDecimals: 2, minDecimals: 2, maxDecimals: 2, q1Decimals: 2, q3Decimals: 2,
    cellFreqDecimals: 4, cellPctDecimals: 4, cellBaseDecimals: 0,
    showUnweightedBase: true, showWeightedBase: true, showBasePct: false,
    hideAutofreq: false,
    globalFilter: '',
    separateExcelPerFilter: false,
    separateExcelPerOutput: false,
    showTotalEachGroup: false,
  };
  const [tableSettings, setTableSettings] = useState(DEFAULT_TABLE_SETTINGS);

  // Survation output conventionally uses whole-number percentages and
  // unrounded (0-decimal) cell values, unlike the standard export defaults
  // (1 / 4 decimals). Toggling Survation on/off nudges those three fields —
  // restoring the standard default on toggle-off only where the value is
  // still at the Survation preset, so a manual override isn't clobbered.
  const handleToggleSurvation = () => {
    setRunTablesSurvation((prev) => {
      const next = !prev;
      setTableSettings((s) => (next ? {
        ...s, pctDecimals: 0, cellFreqDecimals: 0, cellPctDecimals: 0,
      } : {
        ...s,
        pctDecimals: s.pctDecimals === 0 ? DEFAULT_TABLE_SETTINGS.pctDecimals : s.pctDecimals,
        cellFreqDecimals: s.cellFreqDecimals === 0 ? DEFAULT_TABLE_SETTINGS.cellFreqDecimals : s.cellFreqDecimals,
        cellPctDecimals: s.cellPctDecimals === 0 ? DEFAULT_TABLE_SETTINGS.cellPctDecimals : s.cellPctDecimals,
      }));
      return next;
    });
  };
  const [outputSets, setOutputSets] = useState([]);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [runMatrixRows, setRunMatrixRows] = useState([]);
  const [runMatrixModalOpen, setRunMatrixModalOpen] = useState(false);
  const [dataGroupVersionsModalOpen, setDataGroupVersionsModalOpen] = useState(false);
  const [dataGroupVersions, setDataGroupVersions] = useState([]);
  const [dataGroupVersionsLoading, setDataGroupVersionsLoading] = useState(false);
  const [dataGroupVersionBusyId, setDataGroupVersionBusyId] = useState(null);
  const [runMatrixStatus, setRunMatrixStatus] = useState("idle"); // idle|running|done|error
  const [runMatrixProgress, setRunMatrixProgress] = useState(-1);
  const [runMatrixBatchRows, setRunMatrixBatchRows] = useState([]);
  const runMatrixJobRef = useRef(null);
  const [exportOptions, setExportOptions] = useState([
    { text: "Export Percentages", value: "percent" },
    { text: "Export Frequencies", value: "frequency" },
    { text: "Export Both", value: "both" },
  ]);
  const allExportTypes = [
    { type: "Uncle", text: "Uncle Tables", feature: "uncle" },
    { type: "Quantum", text: "Quantum Tables", feature: "quantum" },
    { type: "Wincross", text: "Wincross Tables", feature: "wincross" },
  ];
  const [exportfeatures, setExportfeatures] = useState([]);
  const [crosstabDisplayData, setCrosstabDisplayData] = useState(null);
  const [activeTab, setActiveTab] = useState(
    banners && banners.length > 0 ? banners[0]?.name || "" : ""
  );
  const [displayMode, setDisplayMode] = useState("table");
  const [isCrossTab, setisCrossTab] = useState(false);
  const [exportOption, setExportOption] = useState(false);
  const [fileName, setFileName] = useState("");
  const [displaySettings, setDisplaySettings] = useState({
    showFrequency: true,
    showPercent: true,
  });

  // Merged display settings passed to all table components
  const fullDisplaySettings = {
    ...displaySettings,
    ...tableSettings,
  };
  const [isControlPanelOpen, setIsControlPanelOpen] = useState(false);
  const [allowedExports, setAllowedExports] = useState([]);
  const [hasLanded, setHasLanded] = useState(false);
  const [dbStatus, setDbStatus] = useState(null);
  const [isTabSpecsModalOpen, setIsTabSpecsModalOpen] = useState(false);

  // ── Tab Specs uploader state ──────────────────────────────────────────────
  const [tabSpecsAlreadyUsed, setTabSpecsAlreadyUsed] = useState(false);
  const [tabSpecFile, setTabSpecFile] = useState(null);
  const [questionnaireFile, setQuestionnaireFile] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | processing | success | error
  const [statusMessage, setStatusMessage] = useState("");
  const [progress, setProgress] = useState(0);
  const tabSpecInputRef = useRef(null);
  const questionnaireInputRef = useRef(null);

  // ── Variable selector step ─────────────────────────────────────────────────
  const [tsStep, setTsStep] = useState("upload");  // "upload" | "select"
  const [varSearch, setVarSearch] = useState("");
  const [varFilter, setVarFilter] = useState("all"); // "all" | "ai" | "excluded"

  // Mirrors the backend _is_junk_variable logic to classify vars client-side.
  // Returns: "ai" | "auto" | "user"
  const getVarStatus = (key, value) => {
    if (!value || typeof value !== "object") return "auto";
    if (value.removeTab === true) return "user";
    const type = (value.type || "").toLowerCase();
    if (type === "string" || type === "stringgrid") return "auto";
    const qtitle = (value.Qtitle || "").trim();
    if (qtitle && qtitle.toLowerCase() === key.toLowerCase()) return "auto";
    if (qtitle.toLowerCase().startsWith("bridgeq -")) return "auto";
    try { if (JSON.stringify(value).length > 40000) return "auto"; } catch (_) { }
    return "ai";
  };

  const getAutoReason = (key, value) => {
    const type = (value?.type || "").toLowerCase();
    if (type === "string" || type === "stringgrid") return "open-end text";
    const qtitle = (value?.Qtitle || "").trim();
    if (qtitle && qtitle.toLowerCase() === key.toLowerCase()) return "system var";
    if (qtitle.toLowerCase().startsWith("bridgeq -")) return "bridge routing";
    try { if (JSON.stringify(value).length > 40000) return "too large (>40 KB)"; } catch (_) { }
    return "auto-excluded";
  };

  const toggleVarRemoveTab = (key) => {
    setdataGroup((prev) => ({
      ...prev,
      [key]: { ...prev[key], removeTab: !prev[key]?.removeTab },
    }));
  };

  const setAllFilteredRemoveTab = (filtered, value) => {
    setdataGroup((prev) => {
      const next = { ...prev };
      filtered.forEach(({ key }) => { next[key] = { ...next[key], removeTab: value }; });
      return next;
    });
  };

  // ── Chat state ────────────────────────────────────────────────────────────
  const [chatMessages, setChatMessages] = useState([]);
  // each entry: { role: "user" | "assistant", text: string, isStreaming?: boolean }
  const [chatHistory, setChatHistory] = useState([]);
  // Gemini history array { role, parts } — passed back each turn
  const [chatInput, setChatInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const chatBodyRef = useRef(null);
  const chatInputRef2 = useRef(null);

  // ── Save-to-DB state ──────────────────────────────────────────────────────
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
  const [saveError, setSaveError] = useState("");
  // Retain file text so chat can re-reference tabspec & questionnaire
  const [tabSpecText, setTabSpecText] = useState("");
  const [questionnaireText, setQuestionnaireText] = useState("");

  // ── Toggle modal (reset everything on close) ──────────────────────────────
  const toggleTabSpecsModal = () => {
    setIsTabSpecsModalOpen((prev) => !prev);
    if (!isTabSpecsModalOpen) {
      setTabSpecFile(null);
      setQuestionnaireFile(null);
      setStatus("idle");
      setStatusMessage("");
      setProgress(0);
      setChatMessages([]);
      setChatHistory([]);
      setChatInput("");
      setTabSpecText("");
      setQuestionnaireText("");
      setTsStep("upload");
      setVarSearch("");
      setVarFilter("all");
      if (tabSpecInputRef.current) tabSpecInputRef.current.value = "";
      if (questionnaireInputRef.current) questionnaireInputRef.current.value = "";
    }
  };

  // ── File helpers ──────────────────────────────────────────────────────────
  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleTabSpecChange = (e) => setTabSpecFile(e.target.files[0] || null);
  const handleQuestionnaireChange = (e) => setQuestionnaireFile(e.target.files[0] || null);
  const makeDropHandler = (setter) => (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) setter(file);
  };
  const preventDefaults = (e) => e.preventDefault();

  // ── Initial AI processing ─────────────────────────────────────────────────
  const handleProcess = async () => {
    if (!tabSpecFile) {
      setStatus("error");
      setStatusMessage("Please upload a Tab Specification file before processing.");
      return;
    }

    if (!questionnaireFile) {
      const ok = await confirm({
        title: "No Questionnaire Uploaded",
        message: "You have not uploaded a questionnaire file. Without it, AI will not be able to validate or correct question titles and text. Are you sure you want to continue with tab specs only?",
        confirmText: "Continue without Questionnaire",
        variant: "warning",
      });
      if (!ok) return;
    }

    setStatus("processing");
    setStatusMessage("Preparing survey data and sending to AI backend…");
    setProgress(10);

    // Read both files as text client-side so chat can reuse them without
    // re-uploading. Plain-text files (csv, json) decode directly; binary
    // formats (xlsx, docx) are sent as-is and the backend converts them,
    // so we store the raw base64 for those and let the backend decode again.
    const readAsText = (file) =>
      new Promise((resolve) => {
        const ext = file.name.split(".").pop().toLowerCase();
        if (["csv", "json", "txt"].includes(ext)) {
          const r = new FileReader();
          r.onload = (e) => resolve(e.target.result);
          r.readAsText(file, "utf-8");
        } else {
          // For xlsx / xls / docx — send filename only; backend already has
          // the converted text from the initial call. We'll pass the filename
          // so the backend context message is descriptive.
          resolve(`[binary file: ${file.name}]`);
        }
      });

    const [tsText, qText] = await Promise.all([
      readAsText(tabSpecFile),
      readAsText(questionnaireFile),
    ]);
    setTabSpecText(tsText);
    setQuestionnaireText(qText);

    const jsonBlob = new Blob([JSON.stringify(dataGroup)], { type: "application/json" });
    const formData = new FormData();
    formData.append("json_file", jsonBlob, `survey_data_${projectId || "project"}.json`);
    formData.append("tabspec_file", tabSpecFile);
    if (questionnaireFile) {
      formData.append("questionnaire_file", questionnaireFile);
    }

    const progressInterval = setInterval(() => {
      setProgress((p) => Math.min(p + 5, 85));
    }, 1500);

    try {
      const response = await fetch(`${apiService.baseURL}/api/process-tabspecs/${projectId}`, {
        method: "POST",
        body: formData,
      });

      clearInterval(progressInterval);
      setProgress(90);

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Server error: ${response.status}`);
      }

      const result = await response.json();
      setProgress(100);

      if (result.updatedJson) setdataGroup(result.updatedJson);
      if (result.banners && Array.isArray(result.banners)) setBanners(result.banners);

      setStatus("success");
      setStatusMessage("JSON updated successfully! Banners have been applied to your project.");
      setTabSpecsAlreadyUsed(true);
      if (setMessage) toast.success("Tab specs processed successfully!");

      // Reset chat for this fresh result
      setChatMessages([]);
      setChatHistory([]);
    } catch (err) {
      clearInterval(progressInterval);
      setProgress(0);
      setStatus("error");
      setStatusMessage(err.message || "An unexpected error occurred.");
    }
  };

  // ── Save current JSON & banners to the database ──────────────────────────
  const handleSaveToDb = async () => {
    if (!projectId) {
      setSaveError("No project selected.");
      setSaveStatus("error");
      return;
    }
    setSaveStatus("saving");
    setSaveError("");
    try {
      const response = await fetch(
        `${apiService.baseURL}/api/save-tabspecs/${projectId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updatedJson: dataGroup, banners }),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Server error: ${response.status}`);
      }
      setSaveStatus("saved");
      if (setMessage) toast.success("Tab specs saved to database successfully!");
      // Auto-reset the button label after 3 s
      setTimeout(() => setSaveStatus("idle"), 3000);
    } catch (err) {
      setSaveError(err.message || "Save failed.");
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 4000);
    }
  };

  // ── Reset uploader ────────────────────────────────────────────────────────
  const handleReset = () => {
    setTabSpecFile(null);
    setQuestionnaireFile(null);
    setStatus("idle");
    setStatusMessage("");
    setProgress(0);
    setChatMessages([]);
    setChatHistory([]);
    setChatInput("");
    setTabSpecText("");
    setQuestionnaireText("");
    setTsStep("upload");
    setVarSearch("");
    setVarFilter("all");
    if (tabSpecInputRef.current) tabSpecInputRef.current.value = "";
    if (questionnaireInputRef.current) questionnaireInputRef.current.value = "";
  };

  // ── Chat: send one turn via SSE streaming ─────────────────────────────────
  const handleChatSend = async () => {
    const text = chatInput.trim();
    if (!text || chatBusy) return;

    setChatInput("");
    setChatBusy(true);

    // Append user bubble immediately
    setChatMessages((prev) => [...prev, { role: "user", text }]);
    // Placeholder streaming bubble
    setChatMessages((prev) => [...prev, { role: "assistant", text: "", isStreaming: true }]);

    let fullText = "";

    try {
      const res = await fetch(
        `${apiService.baseURL}/api/stream-chat-tabspecs/${projectId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: text,
            history: chatHistory,
            currentJson: dataGroup,
            banners: banners,
            // C3: strip [binary file: ...] placeholders — the backend can't use them
            tabSpecText: (tabSpecText && tabSpecText.startsWith("[binary file:")) ? "" : tabSpecText,
            questionnaireText: (questionnaireText && questionnaireText.startsWith("[binary file:")) ? "" : questionnaireText,
          }),
        }
      );

      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop(); // keep incomplete last line

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const event = JSON.parse(line.slice(6));

            if (event.type === "token") {
              fullText += event.text;
              // Update the last (streaming) bubble in place
              setChatMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                  role: "assistant",
                  text: fullText,
                  isStreaming: true,
                };
                return updated;
              });
            }

            if (event.type === "done") {
              fullText = event.fullText || fullText;

              // Try to parse as a JSON edit response
              let clean = fullText.trim();
              if (clean.startsWith("```json")) clean = clean.slice(7);
              else if (clean.startsWith("```")) clean = clean.slice(3);
              if (clean.endsWith("```")) clean = clean.slice(0, clean.lastIndexOf("```")).trimEnd();

              let displayText = fullText;
              try {
                const parsed = JSON.parse(clean);
                if (parsed.patches && typeof parsed.patches === "object") {
                  // Patch mode: merge only the changed fields into the current dataGroup
                  setdataGroup((prev) => {
                    const updated = { ...prev };
                    Object.entries(parsed.patches).forEach(([key, patch]) => {
                      if (updated[key] && typeof patch === "object") {
                        updated[key] = { ...updated[key], ...patch };
                      }
                    });
                    return updated;
                  });
                  const patchCount = Object.keys(parsed.patches).length;
                  displayText = `✅ ${patchCount} question${patchCount !== 1 ? "s" : ""} updated.`;
                } else if (parsed.updatedJson) {
                  setdataGroup(parsed.updatedJson);
                  if (parsed.banners && Array.isArray(parsed.banners)) setBanners(parsed.banners);
                  displayText = "✅ JSON updated successfully. The changes have been applied to your project.";
                }
              } catch (_) {
                // conversational reply — display as-is
              }

              // Finalise bubble
              setChatMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                  role: "assistant",
                  text: displayText,
                  isStreaming: false,
                };
                return updated;
              });

              // Accumulate Gemini history for next turn
              setChatHistory((prev) => [
                ...prev,
                { role: "user", parts: [{ text }] },
                { role: "model", parts: [{ text: event.fullText || fullText }] },
              ]);
            }

            if (event.type === "error") {
              throw new Error(event.message);
            }
          } catch (_parseErr) {
            // malformed SSE line — skip
          }
        }
      }
    } catch (err) {
      setChatMessages((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          role: "assistant",
          text: `⚠️ Error: ${err.message}`,
          isStreaming: false,
        };
        return updated;
      });
    } finally {
      setChatBusy(false);
    }
  };

  // Auto-scroll chat body to bottom on new messages
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // ── Other existing effects & handlers ─────────────────────────────────────
  useEffect(() => {
    setActivePage("tables");
  }, []);

  useEffect(() => {
    const organization = user?.user?.organization || user?.organization;
    if (organization) {
      if (
        organization.datatables_exports &&
        Array.isArray(organization.datatables_exports) &&
        organization.datatables_exports.length > 0
      ) {
        setAllowedExports(organization.datatables_exports);
        const filteredExports = allExportTypes.filter((exportType) =>
          organization.datatables_exports.includes(exportType.feature)
        );
        setExportfeatures(filteredExports);
      } else {
        console.warn("⚠️ No datatables_exports found, defaulting to all exports");
        setAllowedExports(allExportTypes.map((item) => item.feature));
        setExportfeatures(allExportTypes);
      }
    } else {
      console.warn("⚠️ No organization found, allowing all exports");
      setAllowedExports(allExportTypes.map((item) => item.feature));
      setExportfeatures(allExportTypes);
    }
  }, [user]);

  useEffect(() => {
    setHasLanded(false);
    // Reset all local display state so nothing from the previous project leaks
    // into the newly selected one (context state is reset in FreqProvider).
    setActiveTab("");
    setFreqdataCount(-1);
    setRunTablesStatus("idle");
    setRunTablesProgress(-1);
    setRunTablesLoading(false);
    setExportExcelLoading(false);
    setExportExcelStatus("idle");
    setExportOverlay(null);
    setFileName("");
    setSharedFile(null);
    setTabSpecsAlreadyUsed(false);
    _stopPolling();
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // has_database/has_data_tables are checked lazily here (rather than bundled
  // into the project-click response on Home) since opening the per-project
  // Postgres connection to check is only needed once we actually land on Tables.
  useEffect(() => {
    if (!projectId) { setDbStatus(null); return; }
    fetch(`${apiService.baseURL}/project/${projectId}/db-status`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => setDbStatus(data))
      .catch(() => setDbStatus(null));
  }, [projectId]);

  // Auto-load output sets from the project DB when the project changes
  useEffect(() => {
    if (!projectId) { setOutputSets([]); return; }
    fetch(`${apiService.baseURL}/get_output_sets/${projectId}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => setOutputSets(data.outputSets || []))
      .catch(() => setOutputSets([]));
  }, [projectId]);

  // Auto-load the saved Run Matrix from the project DB when the project changes
  useEffect(() => {
    if (!projectId) { setRunMatrixRows([]); return; }
    fetch(`${apiService.baseURL}/get_run_matrix/${projectId}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => setRunMatrixRows(data.runs || []))
      .catch(() => setRunMatrixRows([]));
  }, [projectId]);

  // Check how many tables are cached in freqdatatable for the current project+banner.
  // This enables/disables the Export Excel button without requiring a full run first.
  useEffect(() => {
    if (!projectId) { setFreqdataCount(0); return; }
    setFreqdataCount(-1); // loading
    const bn = encodeURIComponent(selectedBanner?.name || "Topline");
    const wt = encodeURIComponent(selectedWeights || "");
    fetch(`${apiService.baseURL}/freqdatatable_count/${projectId}?banner=${bn}&weight=${wt}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => setFreqdataCount(data.count ?? 0))
      .catch(() => setFreqdataCount(0));
  }, [projectId, selectedBanner, selectedWeights]);

  const handleChangeOutputSets = (next) => {
    setOutputSets(next);
    if (!projectId) return;
    fetch(`${apiService.baseURL}/save_output_sets/${projectId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ outputSets: next }),
    }).catch(() => { });
  };

  const handleChangeRunMatrixRows = (next) => {
    setRunMatrixRows(next);
    if (!projectId) return;
    fetch(`${apiService.baseURL}/save_run_matrix/${projectId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ runs: next }),
    }).catch(() => { });
  };

  // Translate the UI's per-row shape (bannerName string) into the execution
  // shape the batch routes expect (banner: full banner object, matching
  // selectedBanner) — resolved at submit time so saved rows stay valid even
  // if `banners` reloads with a different reference.
  const buildRunMatrixExecutionRuns = () =>
    (runMatrixRows || []).map((r) => ({
      label: r.label || r.bannerName || "Topline",
      banner: r.bannerName ? (banners || []).find((b) => b.name === r.bannerName) || null : null,
      weight: r.weight || null,
      filter: r.filter || "ALL",
      outputSetNames: r.outputSetNames || [],
      // Rows saved before these fields existed have no key here — default to
      // included / "use the shared global Sig Testing setting" so old saved
      // matrices run exactly as they did before this change.
      enabled: r.enabled !== false,
      sigTestMode: r.sigTestMode || "default",
      sigTestWeighted: r.sigTestWeighted !== false,
    }));

  const _stopRunMatrixPolling = () => {
    if (runMatrixJobRef.current?.intervalId) {
      clearInterval(runMatrixJobRef.current.intervalId);
    }
    runMatrixJobRef.current = null;
  };

  const handleRunMatrixAll = async () => {
    if (runMatrixStatus === "running" || !runMatrixRows.length) return;
    _stopRunMatrixPolling();
    setRunMatrixStatus("running");
    setRunMatrixProgress(0);
    setRunMatrixBatchRows([]);
    try {
      const payload = {
        projectId,
        runs: buildRunMatrixExecutionRuns(),
        exportType,
        sigTest: runTablesSigTest,
        sigTestWeighted,
        layout: runTablesLayout,
        survationFormat: runTablesSurvation,
        bilendiFormat: isbilendi,
        bilendiSeparateSheet,
        user: powner,
        ...tableSettings,
        outputSets,
        separateExcelPerOutput: tableSettings.separateExcelPerOutput || false,
      };
      const response = await fetch(`${apiService.baseURL}/run_tables_batch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Server error ${response.status}`);
      }
      const data = await response.json();
      const jobId = data.job_id;
      setRunMatrixBatchRows(data.rows || []);

      const intervalId = setInterval(async () => {
        try {
          const r = await fetch(`${apiService.baseURL}/run_tables_batch_progress/${jobId}`);
          if (!r.ok) {
            _stopRunMatrixPolling();
            setRunMatrixStatus("error");
            toast.error(
              r.status === 404
                ? "Lost track of the Run Matrix job (server may have restarted) — please retry."
                : "Lost connection while checking Run Matrix progress — please retry."
            );
            setTimeout(() => setRunMatrixStatus("idle"), 4000);
            return;
          }
          const status = await r.json();
          setRunMatrixProgress(status.progress ?? 0);
          setRunMatrixBatchRows(status.rows || []);
          if (status.status === "done") {
            _stopRunMatrixPolling();
            setRunMatrixStatus("done");
            toast.success("Run Matrix completed — use Download All to export.");
            setTimeout(() => setRunMatrixStatus("idle"), 3000);
          } else if (status.status === "error") {
            _stopRunMatrixPolling();
            setRunMatrixStatus("error");
            toast.error(`Run Matrix failed: ${status.error || "Unknown error"}`);
            setTimeout(() => setRunMatrixStatus("idle"), 4000);
          } else if (status.stalled) {
            _stopRunMatrixPolling();
            setRunMatrixStatus("error");
            toast.error(
              "Run Matrix appears stuck (no progress for a while) — the server may have restarted. Please retry."
            );
            setTimeout(() => setRunMatrixStatus("idle"), 4000);
          }
        } catch (_) {
          // transient network error — keep polling
        }
      }, 2000);
      runMatrixJobRef.current = { jobId, intervalId };
    } catch (err) {
      toast.error(`Run Matrix failed: ${err.message}`);
      setRunMatrixStatus("error");
      setTimeout(() => setRunMatrixStatus("idle"), 4000);
    }
  };

  const handleDownloadRunMatrixAll = async () => {
    if (!runMatrixRows.length) return;
    try {
      const payload = {
        projectId,
        runs: buildRunMatrixExecutionRuns(),
        exportType,
        sigTest: runTablesSigTest,
        sigTestWeighted,
        layout: runTablesLayout,
        survationFormat: runTablesSurvation,
        bilendiFormat: isbilendi,
        bilendiSeparateSheet,
        user: powner,
        ...tableSettings,
        outputSets,
        separateExcelPerOutput: tableSettings.separateExcelPerOutput || false,
      };
      const response = await fetch(`${apiService.baseURL}/export_excel_from_cache_batch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Server error ${response.status}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tables_${projectId}_batch.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(`Download All failed: ${err.message}`);
    }
  };

  useEffect(() => {
    if (!hasLanded && dbStatus && dbStatus.has_database === false) {
      setIsPropertiesShow(true);
      setHasLanded(true);
    } else if (dbStatus) {
      setHasLanded(true);
    }
  }, [dbStatus, hasLanded, setIsPropertiesShow]);

  const scanAndUpdateVarNameLocations = async () => {
    const ok = await confirm({
      title: "Update VarName Locations",
      message: "Are you sure you want to update all varName locations? This action cannot be undone.",
      confirmText: "Update",
      variant: "danger",
    });
    if (!ok) return;
    setLoading(true);
    try {
      const updatedDataGroup = { ...dataGroup };
      let nextLocation = 11;
      let updatedCount = 0;
      let skippedCount = 0;

      const keys = Object.keys(updatedDataGroup);
      const sortedKeys = keys.sort((a, b) => {
        const orderA = updatedDataGroup[a]?.order || updatedDataGroup[a]?._order || 0;
        const orderB = updatedDataGroup[b]?.order || updatedDataGroup[b]?._order || 0;
        return orderA - orderB;
      });

      // console.log("Processing keys in order:", sortedKeys.map(key => ({
      //   key,
      //   order: updatedDataGroup[key]?.order || updatedDataGroup[key]?._order || 0
      // })));

      sortedKeys.forEach(key => {
        const variable = updatedDataGroup[key];
        if (variable && variable.varName && Array.isArray(variable.varName)) {
          const variableType = variable.type || "";
          const isStringType = variableType.toLowerCase().includes("string");
          const isStringGrid = variableType.toLowerCase().includes("stringgrid");

          if (isStringType || isStringGrid) {
            console.log(`Skipping ${key} - ${variableType}`);
            skippedCount++;
            return;
          }
          if (variable.varName.length === 0) return;

          const updatedVarNames = variable.varName.map(varNameObj => {
            if (!varNameObj) return varNameObj;
            const currentLocation = varNameObj.Location || "";
            if (!varNameObj.Location || currentLocation.trim() === "") return varNameObj;

            let newLocation = "";
            if (currentLocation.includes(":")) {
              const parts = currentLocation.split(":");
              if (parts.length === 2) {
                const start = Number(parts[0]);
                const end = Number(parts[1]);
                if (!isNaN(start) && !isNaN(end) && start <= end) {
                  const width = end - start + 1;
                  const newEnd = nextLocation + width - 1;
                  newLocation = `${nextLocation}:${newEnd}`;
                  nextLocation = newEnd + 1;
                  updatedCount++;
                  console.log(`Order ${variable.order || variable._order || 0}: ${key} - ${varNameObj.Name || 'unnamed'} - ${currentLocation} -> ${newLocation}`);
                } else newLocation = currentLocation;
              } else newLocation = currentLocation;
            } else {
              const numLocation = Number(currentLocation);
              if (!isNaN(numLocation) && currentLocation.trim() !== "") {
                newLocation = nextLocation.toString();
                nextLocation += 1;
                updatedCount++;
                console.log(`Order ${variable.order || variable._order || 0}: ${key} - ${varNameObj.Name || 'unnamed'} - ${currentLocation} -> ${newLocation}`);
              } else newLocation = currentLocation;
            }
            return { ...varNameObj, Location: newLocation };
          });

          if (JSON.stringify(variable.varName) !== JSON.stringify(updatedVarNames)) {
            updatedDataGroup[key] = { ...variable, varName: updatedVarNames };
          }
        }
      });

      setdataGroup(updatedDataGroup);
      toast.success(`Updated ${updatedCount} varName locations. Skipped ${skippedCount} String/StringGrid types. Next location: ${nextLocation}`);
    } catch (error) {
      toast.error("Error updating locations. Please check console for details.");
    } finally {
      setLoading(false);
    }
  };

  const closeModalAndUpdateButtons = () => {
    setIsModalOpen(false);
    handleTitleClick(selectedTitle);
  };

  const handleShowProperties = () => {
    setIsPropertiesShow(!isPropertiesShow);
  };

  const handleDisplaySettingsChange = (e) => {
    setDisplaySettings({
      ...displaySettings,
      [e.target.name]: e.target.checked,
    });
  };

  const handleFileUpload = (e) => {
    setLoading(true);
    const input = e.target;
    const file = input.files[0];

    if (!file) return;

    setFileName(file.name);

    const reader = new FileReader();

    reader.onload = async (evt) => {

      try {
        const data = new Uint8Array(evt.target.result);

        const workbook = XLSX.read(data, { type: "array" });

        const summarySheet = XLSX.utils.sheet_to_json(
          workbook.Sheets["Summary"],
          { header: 1 }
        );

        const detailsSheet = XLSX.utils.sheet_to_json(
          workbook.Sheets["Details"],
          { header: 1, blankrows: true }
        );

        const newDataGroup = parseExcelToDataGroup(summarySheet, detailsSheet);

        setdataGroup(newDataGroup);

        // Save updated dataGroup to backend
        try {
          const blob = new Blob([JSON.stringify(newDataGroup)], { type: "application/json" });
          const formData = new FormData();
          formData.append("file", blob, "datagroup.json");
          const saveResp = await fetch(
            `${apiService.baseURL}/api/upload-qgroup-json/${projectId}`,
            { method: "POST", body: formData }
          );
          if (saveResp.ok) {
            toast.success("Excel imported and saved to server.");
          } else {
            toast.warning("Excel imported but server save failed.");
          }
        } catch (saveErr) {
          toast.warning("Excel imported but server save failed.");
        }

        // Refresh the currently open question so its properties reflect the new data
        if (selectedTitle) {
          handleTitleClick(selectedTitle);
        }

      } catch (error) {
        toast.error("Error processing file. Please check format.");
      } finally {
        // 🔥 IMPORTANT FIX (same as previous issue)
        input.value = null;
        setLoading(false);
      }
    };

    reader.onerror = (err) => {
      toast.error("Failed to read file.");

      // 🔥 Reset here as well
      input.value = null;
    };

    reader.readAsArrayBuffer(file);
  };

  const parseExcelToDataGroup = (summarySheet, detailsSheet) => {
    const dataGroup = {};
    const [headerRow, ...dataRows] = summarySheet;

    // True when every cell in a row is absent or empty — catches XLSX's
    // "blank row with nulls" which has length > 0 but no real data.
    const isBlankRow = (row) =>
      !row || row.length === 0 ||
      row.every((cell) => cell === null || cell === undefined || cell === "");

    // ── Summary sheet ────────────────────────────────────────────────────────
    for (const row of dataRows) {
      // Normalize: key must be a non-empty string after trimming
      const rawKey = row[0];
      const key = rawKey != null ? String(rawKey).trim() : "";
      if (!key) continue;

      const item = {};
      headerRow.forEach((header, i) => {
        if (!header || header === "key") return;
        const raw = row[i];
        // Skip missing or empty cells — never write null/undefined into the object
        if (raw === undefined || raw === null || raw === "") return;
        try {
          item[header] = JSON.parse(raw);
        } catch {
          item[header] = typeof raw === "string" ? raw.trim() : raw;
        }
      });

      // Skip ghost rows that yielded no actual properties
      if (Object.keys(item).length === 0) continue;

      dataGroup[key] = item;
    }

    // ── Details sheet ────────────────────────────────────────────────────────
    let i = 0;
    while (i < detailsSheet.length) {
      const keyRow = detailsSheet[i];
      if (isBlankRow(keyRow)) { i++; continue; }

      const rawKey = keyRow[0];
      const key = rawKey != null ? String(rawKey).trim() : "";
      if (!key) { i++; continue; }

      const subHeaderRow = detailsSheet[i + 1];
      if (!subHeaderRow || subHeaderRow[0] == null) { i++; continue; }
      const subHeader = String(subHeaderRow[0]).trim();
      if (!subHeader) { i++; continue; }

      const headers = detailsSheet[i + 2];
      if (!headers || headers.length === 0) { i++; continue; }

      const values = [];
      let j = i + 3;
      while (j < detailsSheet.length) {
        const row = detailsSheet[j];
        if (isBlankRow(row)) break;

        // Two consecutive single-cell rows = new key/subheader block starts
        const isOnlyFirstCell = (r) =>
          r && r.length >= 1 &&
          r[0] !== undefined && r[0] !== null && r[0] !== "" &&
          r.slice(1).every((v) => v === undefined || v === null || v === "");
        if (isOnlyFirstCell(row)) {
          const nextRow = detailsSheet[j + 1];
          if (nextRow && isOnlyFirstCell(nextRow)) break;
        }

        const obj = {};
        headers.forEach((h, idx) => {
          if (!h) return;
          const raw = row[idx];
          if (raw === undefined || raw === null) return; // omit missing cells
          if (typeof raw === "string") {
            try { obj[h] = JSON.parse(raw); } catch { obj[h] = raw; }
          } else {
            obj[h] = raw;
          }
        });
        values.push(obj);
        j++;
      }

      if (!dataGroup[key]) dataGroup[key] = {};
      dataGroup[key][subHeader] = values;
      i = j + 1;
    }

    return dataGroup;
  };

  const exportFrequencies = async (type = "both") => {
    const endpoint = "/export_frequencies";
    const requestData = { projectId: projectId };
    if (type !== "both") requestData.type = type;
    requestData.type = type;
    requestData.sigTest = sigText;
    try {
      const response = await fetch(`${apiService.baseURL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = type === "both" ? `frequency_data_${projectId}.xlsx` : `frequency_data_${projectId}_${type}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export error:", error);
    }
  };

  const downloadTxtFile = (e) => {
    setExportOption(e);
    setExportOverlay(e);
    const body = JSON.stringify({
      projectId,
      dataList: dataGroup,
      banners,
      user: powner,
      project: JSON.stringify(selectedProject),
      client: JSON.stringify(selectedClient),
      bilendi: isbilendi,
      type: e,
    });
    if (e === "Wincross" || e === "Quantum") {
      const downloadName = e === "Quantum" ? "quantum_files.zip" : "tabs.JOB";
      fetch(`${apiService.baseURL}/generate-file`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${apiService.getToken()}` },
        body,
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Server error ${response.status}`);
          return response.blob();
        })
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = downloadName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          setExportOverlay(null);
          toast.success("File generated and downloaded successfully!");
        })
        .catch((error) => {
          toast.error("Error generating file. Contact Administrator.");
          setExportOverlay(null);
        });
    } else {
      fetch(`${apiService.baseURL}/generate-file`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${apiService.getToken()}` },
        body,
      })
        .then((response) => response.json())
        .then((data) => {
          setExportOverlay(null);
          toast.success("File generated successfully!");
        })
        .catch((error) => {
          toast.error("Error generating file. Contact Administrator.");
          setExportOverlay(null);
        });
    }
  };

  const handleTabChange = (tabName) => {
    if (tabName === "") {
      setActiveTab("");
      setselectedBanner(null);
    } else {
      const check = banners.filter((x) => x.name === tabName)[0];
      setActiveTab(check.name);
      setselectedBanner(check);
    }
  };

  const downloadJson = () => {
    if (!dataGroup) {
      console.error('No dataGroup provided');
      return;
    }

    let fileContent = '';
    if (typeof dataGroup === 'string') {
      fileContent = dataGroup;
    } else if (typeof dataGroup === 'object') {
      // Sort the object by 'order' property
      const sortedObj = {};
      const sortedKeys = Object.keys(dataGroup).sort((a, b) => {
        const orderA = dataGroup[a]?.order !== undefined ? Number(dataGroup[a].order) : Infinity;
        const orderB = dataGroup[b]?.order !== undefined ? Number(dataGroup[b].order) : Infinity;

        const validOrderA = !isNaN(orderA) ? orderA : Infinity;
        const validOrderB = !isNaN(orderB) ? orderB : Infinity;

        return validOrderA - validOrderB;
      });

      for (const key of sortedKeys) {
        sortedObj[key] = dataGroup[key];
      }

      fileContent = JSON.stringify(sortedObj, null, 2);
    } else {
      fileContent = String(dataGroup);
    }

    const blob = new Blob([fileContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `dataGroup_${new Date().toISOString().slice(0, 19)}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Stop any in-flight polling when component unmounts or project changes
  const _stopPolling = () => {
    if (runTablesJobRef.current?.intervalId) {
      clearInterval(runTablesJobRef.current.intervalId);
    }
    runTablesJobRef.current = null;
  };

  const _stopRefreshCachePolling = () => {
    if (refreshCacheJobRef.current?.intervalId) {
      clearInterval(refreshCacheJobRef.current.intervalId);
    }
    refreshCacheJobRef.current = null;
  };

  // Cleanup on unmount
  useEffect(() => () => _stopPolling(), []);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => _stopRunMatrixPolling(), []);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => _stopRefreshCachePolling(), []);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleRunTables = async (opts = {}) => {
    if (runTablesLoading) return;
    _stopPolling();  // cancel any previous in-flight job
    setRunTablesLoading(true);
    setRunTablesStatus("running");
    setRunTablesProgress(0);
    setRunTablesMode("fast");
    try {
      const payload = {
        projectId: projectId,
        selectedBanner: selectedBanner || null,
        selectedWeights: selectedWeights || null,
        exportType: opts.exportType ?? exportType,
        sigTest: opts.sigTest ?? runTablesSigTest,
        sigTestWeighted: sigTestWeighted,
        layout: opts.layout ?? runTablesLayout,
        survationFormat: opts.survationFormat ?? runTablesSurvation,
        bilendiFormat: opts.bilendiFormat ?? isbilendi,
        bilendiSeparateSheet: bilendiSeparateSheet,
        refreshData: opts.refreshData ?? false,
        user: powner,
        ...tableSettings,
        outputSets: outputSets,
        separateExcelPerOutput: tableSettings.separateExcelPerOutput || false,
      };
      const response = await fetch(`${apiService.baseURL}/run_all_tables`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Server error ${response.status}`);
      }
      const data = await response.json();

      // ── Background job: poll for progress ──────────────────────────
      if (data.job_id) {
        const jobId = data.job_id;
        const intervalId = setInterval(async () => {
          try {
            const r = await fetch(
              `${apiService.baseURL}/run_all_tables_progress/${jobId}`
            );
            if (!r.ok) {
              _stopPolling();
              toast.error(
                r.status === 404
                  ? "Lost track of this job (server may have restarted) — please retry Run Tables."
                  : "Lost connection while checking Run Tables progress — please retry."
              );
              setRunTablesStatus("error");
              setRunTablesProgress(-1);
              setRunTablesLoading(false);
              setTimeout(() => setRunTablesStatus("idle"), 4000);
              return;
            }
            const status = await r.json();

            setRunTablesProgress(status.progress ?? 0);
            if (status.mode) setRunTablesMode(status.mode);

            if (status.status === "done") {
              _stopPolling();
              const computed = status.computed ?? 0;
              setFreqdataCount(prev => Math.max(prev < 0 ? 0 : prev, computed));
              toast.success(
                `${computed} tables calculated and saved. Click "Export Excel" to download.`
              );
              setRunTablesStatus("done");
              setRunTablesProgress(-1);
              setRunTablesLoading(false);
              setTimeout(() => setRunTablesStatus("idle"), 3000);
            } else if (status.status === "error") {
              _stopPolling();
              toast.error(`Run Tables failed: ${status.error || "Unknown error"}`);
              setRunTablesStatus("error");
              setRunTablesProgress(-1);
              setRunTablesLoading(false);
              setTimeout(() => setRunTablesStatus("idle"), 4000);
            } else if (status.stalled) {
              _stopPolling();
              toast.error(
                "Run Tables appears stuck (no progress for a while) — the server may have restarted. Please retry."
              );
              setRunTablesStatus("error");
              setRunTablesProgress(-1);
              setRunTablesLoading(false);
              setTimeout(() => setRunTablesStatus("idle"), 4000);
            }
          } catch (_) {
            // transient network error — keep polling
          }
        }, 2000);  // poll every 2 s
        runTablesJobRef.current = { jobId, intervalId };
        // NOTE: loading/status remain set — the interval clears them when done
      } else {
        // Synchronous fallback (should not happen with current backend)
        const computed = data.computed ?? 0;
        setFreqdataCount(prev => Math.max(prev < 0 ? 0 : prev, computed));
        toast.success(
          `${computed} tables calculated and saved. Click "Export Excel" to download.`
        );
        setRunTablesStatus("done");
        setRunTablesProgress(-1);
        setRunTablesLoading(false);
        setTimeout(() => setRunTablesStatus("idle"), 3000);
      }
    } catch (err) {
      toast.error(`Run Tables failed: ${err.message}`);
      setRunTablesStatus("error");
      setRunTablesProgress(-1);
      setRunTablesLoading(false);
      setTimeout(() => setRunTablesStatus("idle"), 4000);
    }
    // NOTE: setRunTablesLoading(false) is only called in the polling callbacks above
    // (or in the catch branch) — intentionally NOT in a finally block here.
  };

  // Pre-warms banner_ids_cache for every banner + question filter/custom row/
  // row-statement/flttb logic in the whole project, so a later Run Tables reads
  // a warm cache instead of computing IDs inline (which is what the old
  // "Refresh Banners" toggle used to do, one Run Tables call at a time).
  const handleRefreshFilterCache = async () => {
    if (refreshCacheStatus === "running") return;
    _stopRefreshCachePolling();
    setRefreshCacheStatus("running");
    setRefreshCacheProgress(0);
    setRefreshCacheCounts({ done: 0, total: 0 });
    try {
      const response = await fetch(`${apiService.baseURL}/refresh_filter_cache`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Server error ${response.status}`);
      }
      const data = await response.json();
      const jobId = data.job_id;

      const intervalId = setInterval(async () => {
        try {
          const r = await fetch(`${apiService.baseURL}/refresh_filter_cache_progress/${jobId}`);
          if (!r.ok) {
            _stopRefreshCachePolling();
            toast.error(
              r.status === 404
                ? "Lost track of the Refresh Filter Cache job (server may have restarted) — please retry."
                : "Lost connection while checking Refresh Filter Cache progress — please retry."
            );
            setRefreshCacheStatus("error");
            setRefreshCacheProgress(-1);
            setTimeout(() => setRefreshCacheStatus("idle"), 4000);
            return;
          }
          const status = await r.json();

          setRefreshCacheProgress(status.progress ?? 0);
          setRefreshCacheCounts({ done: status.done ?? 0, total: status.total ?? 0 });

          if (status.status === "done") {
            _stopRefreshCachePolling();
            toast.success(
              status.total
                ? `Filter cache refreshed for ${status.total} filter${status.total !== 1 ? "s" : ""}.`
                : "Filter cache refreshed — no filters found to cache."
            );
            setRefreshCacheStatus("done");
            setRefreshCacheProgress(-1);
            setTimeout(() => setRefreshCacheStatus("idle"), 3000);
          } else if (status.status === "error") {
            _stopRefreshCachePolling();
            toast.error(`Refresh Filter Cache failed: ${status.error || "Unknown error"}`);
            setRefreshCacheStatus("error");
            setRefreshCacheProgress(-1);
            setTimeout(() => setRefreshCacheStatus("idle"), 4000);
          }
        } catch (_) {
          // transient network error — keep polling
        }
      }, 2000);
      refreshCacheJobRef.current = { jobId, intervalId };
    } catch (err) {
      toast.error(`Refresh Filter Cache failed: ${err.message}`);
      setRefreshCacheStatus("error");
      setRefreshCacheProgress(-1);
      setTimeout(() => setRefreshCacheStatus("idle"), 4000);
    }
  };

  const openDataGroupVersions = async () => {
    setDataGroupVersionsModalOpen(true);
    setDataGroupVersionsLoading(true);
    try {
      const response = await fetch(`${apiService.baseURL}/api/datagroup-versions/${projectId}`);
      if (!response.ok) throw new Error(`Server error ${response.status}`);
      const data = await response.json();
      setDataGroupVersions(data.versions || []);
    } catch (err) {
      toast.error(`Failed to load datagroup versions: ${err.message}`);
      setDataGroupVersions([]);
    } finally {
      setDataGroupVersionsLoading(false);
    }
  };

  const handleRestoreDataGroupVersion = async (version) => {
    const ok = await confirm({
      title: "Restore Datagroup Version",
      message: `Restore the datagroup to version ${version.version} (${version.createdAt ? new Date(version.createdAt).toLocaleString() : "unknown time"})? The current datagroup will be saved as a new version first, so this can be undone.`,
      confirmText: "Restore",
      variant: "warning",
    });
    if (!ok) return;

    setDataGroupVersionBusyId(version.id);
    try {
      const response = await fetch(
        `${apiService.baseURL}/api/datagroup-versions/${projectId}/restore/${version.id}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user: powner }),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Server error ${response.status}`);
      }
      const data = await response.json();
      const qgroup = data.qgroup || {};
      const sortedKeys = Object.keys(qgroup)
        .filter((key) => qgroup[key] != null)
        .sort((a, b) => (qgroup[a].order ?? 0) - (qgroup[b].order ?? 0));
      const sortedGroup = sortedKeys.reduce((acc, key) => {
        acc[key] = qgroup[key];
        return acc;
      }, {});
      setdataGroup(sortedGroup);
      toast.success(`Datagroup restored to version ${version.version}`);
      setDataGroupVersionsModalOpen(false);
    } catch (err) {
      toast.error(`Restore failed: ${err.message}`);
    } finally {
      setDataGroupVersionBusyId(null);
    }
  };

  const handleExportExcel = async () => {
    if (exportExcelLoading || freqdataCount === 0) return;
    setExportExcelLoading(true);
    setExportExcelStatus("idle");
    try {
      const payload = {
        projectId: projectId,
        selectedBanner: selectedBanner || null,
        selectedWeights: selectedWeights || null,
        exportType: exportType,
        sigTest: runTablesSigTest,
        sigTestWeighted: sigTestWeighted,
        layout: runTablesLayout,
        survationFormat: runTablesSurvation,
        tolunaFormat: runTablesToluna,
        bilendiFormat: isbilendi,
        bilendiSeparateSheet: bilendiSeparateSheet,
        user: powner,
        ...tableSettings,
        outputSets: outputSets,
        separateExcelPerOutput: tableSettings.separateExcelPerOutput || false,
      };
      const response = await fetch(`${apiService.baseURL}/export_excel_from_cache`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Server error ${response.status}`);
      }
      const contentType = response.headers.get("Content-Type") || "";
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const bn = selectedBanner?.name || "Topline";
      const ext = contentType.includes("zip") ? "zip" : "xlsx";
      a.download = `tables_${projectId}_${bn}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setExportExcelStatus("done");
      setTimeout(() => setExportExcelStatus("idle"), 3000);
    } catch (err) {
      toast.error(`Export Excel failed: ${err.message}`);
      setExportExcelStatus("error");
      setTimeout(() => setExportExcelStatus("idle"), 4000);
    } finally {
      setExportExcelLoading(false);
    }
  };

  const handleDownloadVtm = async () => {
    if (vtmDownloading) return;
    setVtmDownloading(true);
    try {
      const response = await fetch(`${apiService.baseURL}/vtm-download/${projectId}`);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Server error ${response.status}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vtm_project_${projectId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(`Download VTM failed: ${err.message}`);
    } finally {
      setVtmDownloading(false);
    }
  };

  const handleExportTypeChange = (event) => {
    setExportType(event);
    exportFrequencies(event);
  };

  const toggleModal = () => setIsModalOpen((prev) => !prev);
  const atoggleModal = () => setIsaModalOpen((prev) => !prev);
  const acloseModalAndUpdateButtons = () => setIsaModalOpen(false);



  const handleUploadWithFetch = async (e) => {

    setLoading(true);
    const selectedFile = e.target.files[0];
    if (!selectedFile || !projectId) return;

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('banner', JSON.stringify(saveBanner));
    let apiservice = saveBanner ? 'upload-banner-json' : 'upload-qgroup-json';



    try {
      const response = await fetch(`${apiService.baseURL}/api/${apiservice}/${projectId}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const data = await response.json();
      toast.success('Successfully uploaded');
    } catch (error) {
      toast.error(error.message);
    } finally {

      e.target.value = null;
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    const DETAIL_FIELDS = ["Rows", "values", "reverseGrid", "grid", "Cols", "varName", "rankSumary", "valueLabels", "Nets", "customRows", "RowCalcs"];

    const sortedEntries = Object.entries(dataGroup)
      .sort((a, b) => {
        const orderA = a[1]?.order !== undefined ? Number(a[1].order) : Infinity;
        const orderB = b[1]?.order !== undefined ? Number(b[1].order) : Infinity;
        const validOrderA = !isNaN(orderA) ? orderA : Infinity;
        const validOrderB = !isNaN(orderB) ? orderB : Infinity;
        return validOrderA - validOrderB;
      });

    // ── Summary sheet ────────────────────────────────────────────────────────
    const rows = [];
    const headersSet = new Set();

    for (const [key, item] of sortedEntries) {
      if (!item || typeof item !== "object") continue;
      const row = { key };
      for (const prop in item) {
        if (DETAIL_FIELDS.includes(prop)) continue;
        row[prop] = (typeof item[prop] === "object" && item[prop] !== null)
          ? JSON.stringify(item[prop])
          : item[prop];
        headersSet.add(prop);
      }
      rows.push(row);
    }

    const headers = ["key", ...Array.from(headersSet)];
    const summaryData = [headers];
    for (const row of rows) {
      summaryData.push(headers.map((h) => row[h] ?? ""));
    }

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    summarySheet["!cols"] = headers.map((h) => {
      if (h === "key") return { wch: 22 };
      if (["Qtitle", "tabSpectitle", "basetitle"].includes(h)) return { wch: 50 };
      if (h === "type") return { wch: 20 };
      if (h === "oeparameters") return { wch: 42 };
      return { wch: 18 };
    });

    // ── Details sheet ────────────────────────────────────────────────────────
    const detailData = [];
    for (const [key, item] of sortedEntries) {
      if (!item || typeof item !== "object") continue;
      for (const field of DETAIL_FIELDS) {
        if (!(field in item)) continue;
        const subHeaders = item[field];
        if (!Array.isArray(subHeaders) || subHeaders.length === 0) continue;
        detailData.push([key]);
        detailData.push([field]);
        const columnHeaders = Object.keys(subHeaders[0]);
        detailData.push(columnHeaders);
        for (const obj of subHeaders) {
          detailData.push(columnHeaders.map((col) => {
            const val = obj[col];
            if (val !== null && val !== undefined && typeof val === "object") return JSON.stringify(val);
            return val ?? "";
          }));
        }
        detailData.push([]);
      }
    }

    const detailSheet = XLSX.utils.aoa_to_sheet(detailData);
    detailSheet["!cols"] = [{ wch: 22 }, { wch: 18 }, { wch: 42 }, { wch: 18 }, { wch: 18 }, { wch: 18 }];

    // ── Guide sheet ──────────────────────────────────────────────────────────
    const guideData = [
      ["ERAAI Data Tables — Excel Import / Export Guide"],
      [""],
      ["SUMMARY SHEET — Column Reference"],
      ["Column", "Required?", "Description", "Valid Values / Example"],
      ["key", "Required", "Unique variable name — must match your SPSS/MDD variable exactly. Case-sensitive.", "Q1, Q_gender, Q_age"],
      ["Qtitle", "Optional", "Question text shown in the properties panel.", "What is your age?"],
      ["type", "Required", "Question type — controls how the table is computed and rendered.", "See Valid Types below"],
      ["order", "Optional", "Numeric display order. Lower numbers appear first.", "1, 2, 3 …"],
      ["basetitle", "Optional", "Label for the base row shown beneath the table.", "All respondents"],
      ["tabSpectitle", "Optional", "Title used when processing tab specifications.", "Overall satisfaction"],
      ["removeTab", "Optional", "TRUE = exclude from table runs and exports. Default: FALSE.", "TRUE  or  FALSE"],
      ["oeparameters", "Optional (JSON)", "Advanced statistics / display settings stored as a JSON string.", '{"mean":true,"median":false}'],
      [""],
      ["VALID QUESTION TYPES"],
      ["Type", "Use Case"],
      ["SingleResponse", "Single-answer question (radio / single-punch)"],
      ["Multiple", "Multi-answer / checkbox question"],
      ["Numeric", "Numeric or open-end numeric field"],
      ["Single2DGrid", "Grid — one response per row"],
      ["Multiple2DGrid", "Grid — multiple responses per row (checkboxes in a grid)"],
      ["Numeric2DGrid", "Grid — numeric value per row"],
      ["Multiple3DGrid", "3-dimensional grid — multiple responses"],
      ["Numeric3DGrid", "3-dimensional grid — numeric values"],
      ["Custom", "Custom computed / derived variable"],
      ["String", "Open-end text — auto-excluded from table runs"],
      [""],
      ["DETAILS SHEET — Block Structure"],
      ["The Details sheet stores array fields (Rows, Cols, varName, Nets, etc.) for each question."],
      ["Each block follows this pattern:"],
      ["  Row 1:  question key  (e.g. Q1)"],
      ["  Row 2:  field name    (e.g. varName, Rows, Nets)"],
      ["  Row 3:  column headers for that array"],
      ["  Rows 4+: one data object per row"],
      ["  Blank row: block separator — do not remove"],
      [""],
      ["IMPORT TIPS"],
      ["• Do NOT change the key column — it links Summary rows to their Details blocks."],
      ["• Leave a cell blank to omit a property. Never type 'null' or 'undefined'."],
      ["• Boolean columns accept TRUE/FALSE (Excel built-in) or 1/0."],
      ["• JSON columns (oeparameters etc.) must be valid JSON strings."],
      ["• Rows with a blank or whitespace-only key are automatically skipped on import."],
      ["• Add new questions by appending rows at the bottom of the Summary sheet."],
      ["• After importing, click 'Run Tables' to recompute frequency data."],
    ];

    const guideSheet = XLSX.utils.aoa_to_sheet(guideData);
    guideSheet["!cols"] = [{ wch: 22 }, { wch: 12 }, { wch: 68 }, { wch: 38 }];

    // ── Build workbook ────────────────────────────────────────────────────────
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
    XLSX.utils.book_append_sheet(workbook, detailSheet, "Details");
    XLSX.utils.book_append_sheet(workbook, guideSheet, "Guide");
    XLSX.writeFile(workbook, `DataGroupExport_${projectId || "project"}.xlsx`);
  };
  // const features = user?.user?.organization?.data_tables_feature || user?.organization.data_tables_feature || {};
  // const expfeatures = allExportTypes.filter(item => features[item.feature] === true);
  const features = user?.user?.organization?.data_tables_feature || user?.organization?.data_tables_feature;
  if (!features) {
    return <div>Loading...</div>;
  }
  const expfeatures = allExportTypes.filter(item => features[item.feature] === true);
  
  return (
    <>
      <div>
        {/* Control Panel */}
        {isControlPanelOpen && (
          <div className="sm-overlay" onClick={() => setIsControlPanelOpen(false)}>
            <div className="sm-modal" onClick={e => e.stopPropagation()}>
              <div className="sm-header">
                <span className="sm-title">
                  <FontAwesomeIcon icon={faGear} className="sm-title-icon" />
                  Settings
                </span>
                <button className="sm-close" onClick={() => setIsControlPanelOpen(false)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="sm-body">

                {/* ── Row 1: Data Management + Analysis Tools ── */}
                <div className="sm-row2">
                  <div className="sm-cell">
                    <div className="sm-cell-title">
                      <FontAwesomeIcon icon={faFileExport} /> Data Management
                    </div>
                    <div className="control-group">
                      <div className="dm-action-row">
                        {features?.export_excel && (
                          <button className="dm-action-btn dm-action-btn--primary" onClick={() => exportToExcel()} title="Export data to Excel">
                            <span className="dm-action-icon"><FontAwesomeIcon icon={faFileExport} /></span>
                            <span className="dm-action-label">Export Excel</span>
                          </button>
                        )}
                        {features?.json_ai && (
                          <button className="dm-action-btn dm-action-btn--success" onClick={downloadJson} title="Download JSON for AI">
                            <span className="dm-action-icon"><FontAwesomeIcon icon={faRobot} /></span>
                            <span className="dm-action-label">JSON AI</span>
                          </button>
                        )}
                      </div>
                      <div className="dm-upload-group">
                        <div className="dm-upload-label-row">
                          <FontAwesomeIcon icon={faUpload} className="dm-section-icon" />
                          <span className="dm-section-title">Import / Upload</span>
                        </div>
                        <input ref={sharedFileRef} type="file" style={{ display: "none" }} onChange={onSharedFileChange} />
                        {features?.import_excel && (
                          <label className="dm-action-btn dm-action-btn--upload dm-action-btn--full" title="Choose Excel file to import">
                            <span className="dm-action-icon"><FontAwesomeIcon icon={faFileExcel} /></span>
                            <span className="dm-action-label">{fileName ? fileName : "Import Excel"}</span>
                            <input type="file" accept=".xlsx,.xls" onChange={handleFileUpload} style={{ display: "none" }} />
                          </label>
                        )}
                        {features?.select_json && (
                          <div
                            className={`dm-dropzone ${sharedFile ? "dm-dropzone--has-file" : ""}`}
                            onClick={() => openSharedPicker(".json")}
                            title="Click to select a JSON file"
                          >
                            <FontAwesomeIcon icon={sharedFile ? faCheckCircle : faFileAlt} className={`dm-dropzone-icon ${sharedFile ? "dm-dropzone-icon--ok" : ""}`} />
                            <span className="dm-dropzone-text">{sharedFile ? sharedFile.name : "Click to select JSON file"}</span>
                            {sharedFile && (
                              <span className="dm-dropzone-clear" title="Clear selection" onClick={(e) => { e.stopPropagation(); setSharedFile(null); }}>
                                <FontAwesomeIcon icon={faTimes} />
                              </span>
                            )}
                          </div>
                        )}
                        <div className="dm-dispatch-grid">
                          {features?.tab_specs && (
                            <button className={`dm-dispatch-btn dm-dispatch-btn--tabspecs`} onClick={() => { setIsControlPanelOpen(false); dispatchTabSpecs(); }} title="Send file to Tab Specs">
                              <FontAwesomeIcon icon={faTable} />
                              <span>Tab Specs</span>
                            </button>
                          )}
                          {features?.table_json && (
                            <button className={`dm-dispatch-btn ${!sharedFile ? "dm-dispatch-btn--disabled" : "dm-dispatch-btn--tablejson"}`} onClick={dispatchTableJson} title="Upload as Table JSON" disabled={!sharedFile}>
                              <FontAwesomeIcon icon={faFileAlt} />
                              <span>Table JSON</span>
                            </button>
                          )}
                          {features?.banner && (
                            <button className={`dm-dispatch-btn ${!sharedFile ? "dm-dispatch-btn--disabled" : "dm-dispatch-btn--banner"}`} onClick={dispatchBannerJson} title="Upload as Banner JSON" disabled={!sharedFile}>
                              <FontAwesomeIcon icon={faArrowsLeftRight} />
                              <span>Banner</span>
                            </button>
                          )}
                          {features?.scripting && (
                            <button className={`dm-dispatch-btn ${!sharedFile ? "dm-dispatch-btn--disabled" : "dm-dispatch-btn--scripting"}`} onClick={dispatchScriptingJson} title="Save to sqgroup_json (Scripting)" disabled={!sharedFile}>
                              <FontAwesomeIcon icon={faSave} />
                              <span>Scripting</span>
                            </button>
                          )}
                        </div>
                        {features?.import_oe && (
                          <button className="dm-action-btn dm-action-btn--outline dm-action-btn--full" onClick={() => setIsOeImportOpen(true)} title="Import externally-coded open-end data">
                            <span className="dm-action-icon"><FontAwesomeIcon icon={faComments} /></span>
                            <span className="dm-action-label">Import OE Data</span>
                          </button>
                        )}
                        <button className="dm-action-btn dm-action-btn--outline dm-action-btn--full" onClick={() => setIsExcelVarUploadOpen(true)} title="Upload an Excel workbook (id column + variable columns) — matches respondents by an SPSS ID variable and adds/replaces those variables directly in table_single">
                          <span className="dm-action-icon"><FontAwesomeIcon icon={faFileExcel} /></span>
                          <span className="dm-action-label">Upload Excel Data</span>
                        </button>
                        <button className="dm-action-btn dm-action-btn--outline dm-action-btn--full" onClick={handleDownloadVtm} disabled={vtmDownloading} title="Download the variable-to-table map (vtm) so the team can see which table each variable's data lives in">
                          <span className="dm-action-icon"><FontAwesomeIcon icon={vtmDownloading ? faSpinner : faDownload} spin={vtmDownloading} /></span>
                          <span className="dm-action-label">{vtmDownloading ? "Downloading…" : "Download VTM"}</span>
                        </button>
                      </div>
                      {features?.update_locations && (
                        <button className="dm-action-btn dm-action-btn--warning dm-action-btn--full" onClick={scanAndUpdateVarNameLocations} title="Scan all varNames and update locations" disabled={loading}>
                          <span className="dm-action-icon"><FontAwesomeIcon icon={faArrowsLeftRight} /></span>
                          <span className="dm-action-label">Update Locations (Numeric Only)</span>
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="sm-cell">
                    <div className="sm-cell-title">
                      <FontAwesomeIcon icon={faGear} /> Analysis Tools
                    </div>
                    <div className="control-group">
                      <div className="dm-action-row">
                        {features?.properties && (
                          <button className="dm-action-btn dm-action-btn--outline" onClick={() => toggleModal()} title="Update Properties">
                            <span className="dm-action-icon"><FontAwesomeIcon icon={faGear} /></span>
                            <span className="dm-action-label">Properties</span>
                          </button>
                        )}
                        {features?.append_variable && (
                          <button className="dm-action-btn dm-action-btn--outline" onClick={() => atoggleModal()} title="Append Variable">
                            <span className="dm-action-icon"><FontAwesomeIcon icon={faPlus} /></span>
                            <span className="dm-action-label">Append Variable</span>
                          </button>
                        )}
                        <button className="dm-action-btn dm-action-btn--outline" onClick={() => setIsDeleteIdsOpen(true)} title="Delete respondents by ID (removed rows are archived and restorable)">
                          <span className="dm-action-icon"><FontAwesomeIcon icon={faTrash} /></span>
                          <span className="dm-action-label">Delete IDs</span>
                        </button>
                        <button className="dm-action-btn dm-action-btn--outline" onClick={() => setIsMoveVarOpen(true)} title="Move a variable's data from table_single into table_grid_single or table_grid_multiple">
                          <span className="dm-action-icon"><FontAwesomeIcon icon={faArrowsLeftRight} /></span>
                          <span className="dm-action-label">Move to Grid</span>
                        </button>
                      </div>
                      <div className="at-toggle-cards">
                        {features?.auto_refresh && (
                          <label className={`at-toggle-card ${refreshData ? "at-toggle-card--on" : ""}`} onClick={() => setRefreshData(v => !v)}>
                            <div className="at-toggle-card-left">
                              <span className="at-toggle-card-icon"><FontAwesomeIcon icon={faRefresh} /></span>
                              <div>
                                <span className="at-toggle-card-title">Auto Refresh</span>
                                <span className="at-toggle-card-desc">Live data updates</span>
                              </div>
                            </div>
                            <div className="at-toggle-wrap">
                              <div className={`dm-toggle-track ${refreshData ? "dm-toggle-track--on" : ""}`}><div className="dm-toggle-thumb" /></div>
                            </div>
                          </label>
                        )}
                        {features?.bilendi_setup && (
                          <label className={`at-toggle-card ${isbilendi ? "at-toggle-card--on" : ""}`} onClick={() => setBilendi(v => !v)}>
                            <div className="at-toggle-card-left">
                              <span className="at-toggle-card-icon"><FontAwesomeIcon icon={faGlobe} /></span>
                              <div>
                                <span className="at-toggle-card-title">Bilendi Setup</span>
                                <span className="at-toggle-card-desc">Bilendi configuration</span>
                              </div>
                            </div>
                            <div className="at-toggle-wrap">
                              <div className={`dm-toggle-track ${isbilendi ? "dm-toggle-track--on" : ""}`}><div className="dm-toggle-thumb" /></div>
                            </div>
                          </label>
                        )}
                        {features?.bilendi_setup && isbilendi && (
                          <label className={`at-toggle-card ${bilendiSeparateSheet ? "at-toggle-card--on" : ""}`} onClick={() => setBilendiSeparateSheet(v => !v)}>
                            <div className="at-toggle-card-left">
                              <span className="at-toggle-card-icon"><FontAwesomeIcon icon={faFileExcel} /></span>
                              <div>
                                <span className="at-toggle-card-title">Separate Sheet</span>
                                <span className="at-toggle-card-desc">One question per sheet (Client Reference format)</span>
                              </div>
                            </div>
                            <div className="at-toggle-wrap">
                              <div className={`dm-toggle-track ${bilendiSeparateSheet ? "dm-toggle-track--on" : ""}`}><div className="dm-toggle-thumb" /></div>
                            </div>
                          </label>
                        )}
                        {features?.survation_format && (
                          <label className={`at-toggle-card ${runTablesSurvation ? "at-toggle-card--on" : ""}`} onClick={handleToggleSurvation}>
                            <div className="at-toggle-card-left">
                              <span className="at-toggle-card-icon"><FontAwesomeIcon icon={faFileExcel} /></span>
                              <div>
                                <span className="at-toggle-card-title">Survation Format</span>
                                <span className="at-toggle-card-desc">Survation-style table export</span>
                              </div>
                            </div>
                            <div className="at-toggle-wrap">
                              <div className={`dm-toggle-track ${runTablesSurvation ? "dm-toggle-track--on" : ""}`}><div className="dm-toggle-thumb" /></div>
                            </div>
                          </label>
                        )}
                        {features?.toluna_format && (
                          <label className={`at-toggle-card ${runTablesToluna ? "at-toggle-card--on" : ""}`} onClick={() => setRunTablesToluna(v => !v)}>
                            <div className="at-toggle-card-left">
                              <span className="at-toggle-card-icon"><FontAwesomeIcon icon={faFileExcel} /></span>
                              <div>
                                <span className="at-toggle-card-title">Toluna Format</span>
                                <span className="at-toggle-card-desc">Toluna-style export (TOC + stacked tables)</span>
                              </div>
                            </div>
                            <div className="at-toggle-wrap">
                              <div className={`dm-toggle-track ${runTablesToluna ? "dm-toggle-track--on" : ""}`}><div className="dm-toggle-thumb" /></div>
                            </div>
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Row 2: Banner + Weights + Export Type (3 columns) ── */}
                <div className="sm-row3">
                  {features?.select_banner && (
                    <div className="sm-cell">
                      <div className="sm-cell-title">
                        <FontAwesomeIcon icon={faArrowsLeftRight} /> Select Banner
                      </div>
                      <div className="ds-select-grid">
                        {banners && banners.length > 0 ? (
                          <>
                            <button
                              className="ds-chip"
                              style={!activeTab
                                ? { background: "#475569", borderColor: "transparent", color: "#f1f5f9", boxShadow: "0 3px 10px rgba(71,85,105,0.35)" }
                                : { color: "#64748b", borderColor: "#94a3b8" }}
                              onClick={() => handleTabChange("")}
                              title="No banner — show Topline only"
                            >
                              None
                            </button>
                            {banners.map((b, i) => (
                              <button
                                key={i}
                                className={`ds-chip ${activeTab === b.name ? "ds-chip--active" : ""}`}
                                onClick={() => handleTabChange(activeTab === b.name ? "" : b.name)}
                                title={activeTab === b.name ? `${b.name} — click to deselect` : b.name}
                              >
                                {b.name}
                              </button>
                            ))}
                          </>
                        ) : (
                          <span className="ds-empty">No banners available</span>
                        )}
                      </div>
                    </div>
                  )}
                  {features?.select_weights && (
                    <div className="sm-cell">
                      <div className="sm-cell-title">
                        <FontAwesomeIcon icon={faWeightScale} /> Select Weights
                      </div>
                      <div className="ds-select-grid">
                        {savedWeightings && Object.keys(savedWeightings).length > 0 ? (
                          Object.keys(savedWeightings).map((w, i) => (
                            <button
                              key={i}
                              className={`ds-chip ${selectedWeights === w ? "ds-chip--active" : ""}`}
                              onClick={() => setSelectedWeights(selectedWeights === w ? null : w)}
                              title={selectedWeights === w ? `${w} — click to deselect` : w}
                            >
                              {w}
                            </button>
                          ))
                        ) : (
                          <span className="ds-empty">No weights available</span>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                        <input
                          type="text"
                          value={weightVarInput}
                          onChange={(e) => setWeightVarInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && weightVarInput.trim()) {
                              setSelectedWeights(weightVarInput.trim());
                            }
                          }}
                          placeholder="Enter weight variable…"
                          style={{
                            flex: 1,
                            background: "#131f2e",
                            border: "1px solid #334155",
                            borderRadius: 6,
                            color: "#e2e8f0",
                            fontSize: 11,
                            padding: "5px 8px",
                            outline: "none",
                          }}
                        />
                        <button
                          onClick={() => {
                            if (weightVarInput.trim()) setSelectedWeights(weightVarInput.trim());
                          }}
                          title="Set weight variable"
                          style={{
                            background: "#1d4ed8",
                            border: "none",
                            borderRadius: 6,
                            color: "#fff",
                            fontSize: 11,
                            padding: "5px 10px",
                            cursor: "pointer",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Set
                        </button>
                        {selectedWeights && (
                          <button
                            onClick={() => { setSelectedWeights(null); setWeightVarInput(""); }}
                            title="Clear weight"
                            style={{
                              background: "#334155",
                              border: "none",
                              borderRadius: 6,
                              color: "#94a3b8",
                              fontSize: 11,
                              padding: "5px 8px",
                              cursor: "pointer",
                            }}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        )}
                      </div>
                      {selectedWeights && (
                        <div style={{ fontSize: 10, color: "#60a5fa", marginTop: 4 }}>
                          Active: <strong>{selectedWeights}</strong>
                        </div>
                      )}
                    </div>
                  )}
                  {features?.select_weights && (
                    <div className="sm-cell">
                      <div className="sm-cell-title">
                        <FontAwesomeIcon icon={faFileExport} /> Export Type
                      </div>
                      <div className="ds-select-grid">
                        {expfeatures && expfeatures.length > 0 ? (
                          expfeatures.map((x, i) => (
                            <button
                              key={i}
                              className={`ds-chip ${exportOption === x.type ? "ds-chip--active" : ""}`}
                              onClick={() => downloadTxtFile(x.type)}
                              title={x.text}
                            >
                              {x.text}
                            </button>
                          ))
                        ) : (
                          <span className="ds-empty">No export types available</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* ── Row 3: Run Tables — full-width horizontal layout ── */}
                {features?.run_tables && (
                  <div className="sm-cell">
                    <div className="sm-cell-title">
                      <FontAwesomeIcon icon={faFileExcel} /> Run Tables
                      <span style={{ marginLeft: 4, fontSize: 9, color: "#8a9bb0", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(all questions → Excel)</span>
                    </div>
                    <div className="rt-grid">
                      {/* Col 1: Data type + Layout chips */}
                      <div className="rt-col">
                        <div className="rt-sub-label">Data</div>
                        <div className="ds-select-grid" style={{ marginBottom: 8 }}>
                          {[
                            { label: "Freq + %", value: "both" },
                            { label: "% Only", value: "percent" },
                            { label: "Freq Only", value: "frequency" },
                          ].map((opt) => (
                            <button
                              key={opt.value}
                              className={`ds-chip ${exportType === opt.value ? "ds-chip--active" : ""}`}
                              onClick={() => setExportType(opt.value)}
                              title={opt.label}
                              disabled={runTablesLoading}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                        <div className="rt-sub-label">Layout</div>
                        <div className="ds-select-grid">
                          {[
                            { label: "Row format", value: "rows", title: "Freq / % / Sig on separate rows per response" },
                            { label: "Col format", value: "cols", title: "Freq | % | Sig as sub-columns per banner break" },
                          ].map((opt) => (
                            <button
                              key={opt.value}
                              className={`ds-chip ${runTablesLayout === opt.value ? "ds-chip--active" : ""}`}
                              onClick={() => setRunTablesLayout(opt.value)}
                              title={opt.title}
                              disabled={runTablesLoading}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      {/* Col 2: Toggles + conditional Sig symbols */}
                      <div className="rt-col">
                        <div className="rt-sub-label">Options</div>
                        <div className="rt-toggles">
                          {[
                            { label: "Sig Testing", value: runTablesSigTest, set: setRunTablesSigTest, title: "Include significance test letters in export" },
                            { label: "Overlap Correction", value: overlapCorrection, set: setOverlapCorrection, title: "Apply overlap (CORR) correction when banner columns are nested (e.g. Total vs sub-groups). On = columns treated as nested; Off = all columns treated as independent." },
                          ].map(({ label, value, set, title }) => (
                            <div
                              key={label}
                              title={title}
                              style={{ display: "flex", alignItems: "center", gap: 8, cursor: runTablesLoading ? "not-allowed" : "pointer", userSelect: "none" }}
                              onClick={() => !runTablesLoading && set((v) => !v)}
                            >
                              <div style={{ width: 32, height: 17, borderRadius: 9, background: value ? "#2563eb" : "#334155", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
                                <div style={{ position: "absolute", top: 2, left: value ? 17 : 2, width: 13, height: 13, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
                              </div>
                              <span style={{ fontSize: 11, color: value ? "#60a5fa" : "#8a9bb0" }}>{label}</span>
                            </div>
                          ))}
                          {runTablesSigTest && selectedWeights && (
                            <div style={{ marginLeft: 40, marginTop: 2 }}>
                              <div style={{ fontSize: 10, color: "#8a9bb0", marginBottom: 3 }}>Sig symbols</div>
                              <div className="ds-select-grid">
                                {[
                                  { label: "Weighted", value: true },
                                  { label: "Unweighted", value: false },
                                ].map((opt) => (
                                  <button
                                    key={opt.label}
                                    className={`ds-chip ${sigTestWeighted === opt.value ? "ds-chip--active" : ""}`}
                                    onClick={() => !runTablesLoading && setSigTestWeighted(opt.value)}
                                    disabled={runTablesLoading}
                                    title={opt.value ? "Use weighted sig symbols (colSymbolWt)" : "Use unweighted sig symbols (colSymbol) even when weights are active"}
                                  >
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Col 3: Action buttons */}
                      <div className="rt-col rt-col--actions">
                        <button
                          onClick={() => setSettingsModalOpen(true)}
                          disabled={runTablesLoading}
                          title="Decimal places, base rows, statistics display"
                          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 7, padding: "7px 12px", borderRadius: 6, border: "1px solid #334155", background: "#131f2e", color: "#94a3b8", fontSize: 11, cursor: runTablesLoading ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}
                        >
                          <FontAwesomeIcon icon={faGear} style={{ fontSize: 11 }} />
                          Formatting &amp; Display Settings
                        </button>
                        <button
                          onClick={() => setRunMatrixModalOpen(true)}
                          disabled={runTablesLoading}
                          title="Run and export many banner x weight x filter combinations together"
                          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 7, padding: "7px 12px", borderRadius: 6, border: "1px solid #334155", background: "#131f2e", color: "#94a3b8", fontSize: 11, cursor: runTablesLoading ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}
                        >
                          <FontAwesomeIcon icon={faArrowsLeftRight} style={{ fontSize: 11 }} />
                          Run Matrix{runMatrixRows.length > 0 ? ` (${runMatrixRows.length})` : ""}
                        </button>
                        <button
                          onClick={openDataGroupVersions}
                          disabled={runTablesLoading}
                          title="View and restore past datagroup versions (saved automatically on each Run Tables)"
                          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 7, padding: "7px 12px", borderRadius: 6, border: "1px solid #334155", background: "#131f2e", color: "#94a3b8", fontSize: 11, cursor: runTablesLoading ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}
                        >
                          <FontAwesomeIcon icon={faClockRotateLeft} style={{ fontSize: 11 }} />
                          Datagroup Versions
                        </button>
                        <button
                          onClick={handleRefreshFilterCache}
                          disabled={refreshCacheStatus === "running"}
                          title="Pre-compute and cache respondent IDs for every banner, question filter, custom row, row/statement logic and flttb filter in the project — run this once so Run Tables reads a warm cache instead of computing IDs inline."
                          style={{
                            display: "flex", alignItems: "center", justifyContent: "center", gap: 7, padding: "7px 12px", borderRadius: 6,
                            border: refreshCacheStatus === "error" ? "1px solid #b91c1c" : "1px solid #334155",
                            background: "#131f2e", color: refreshCacheStatus === "done" ? "#4ade80" : refreshCacheStatus === "error" ? "#f87171" : "#94a3b8",
                            fontSize: 11, cursor: refreshCacheStatus === "running" ? "not-allowed" : "pointer", whiteSpace: "nowrap",
                            position: "relative", overflow: "hidden",
                          }}
                        >
                          {refreshCacheStatus === "running" && refreshCacheProgress >= 0 && (
                            <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${refreshCacheProgress}%`, background: "rgba(37,99,235,0.25)", transition: "width 0.4s ease", pointerEvents: "none" }} />
                          )}
                          {refreshCacheStatus === "running" ? (
                            <><FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 11 }} />
                              Refreshing Cache…{refreshCacheCounts.total > 0 ? ` (${refreshCacheCounts.done}/${refreshCacheCounts.total})` : ""}</>
                          ) : refreshCacheStatus === "done" ? (
                            <><FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: 11 }} />Cache Refreshed!</>
                          ) : refreshCacheStatus === "error" ? (
                            <><FontAwesomeIcon icon={faTimes} style={{ fontSize: 11 }} />Failed — Retry</>
                          ) : (
                            <><FontAwesomeIcon icon={faRefresh} style={{ fontSize: 11 }} />Refresh Filter Cache</>
                          )}
                        </button>
                        <button
                          className="runtables-btn"
                          onClick={() => handleRunTables({ refreshData: true })}
                          disabled={runTablesLoading || exportExcelLoading}
                          title="Calculate frequencies for all questions and save to cache"
                          style={{
                            display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "9px 14px", borderRadius: 8, border: "none",
                            cursor: (runTablesLoading || exportExcelLoading) ? "not-allowed" : "pointer", fontWeight: 600, fontSize: 13,
                            background: runTablesStatus === "done" ? "linear-gradient(135deg,#16a34a,#15803d)" : runTablesStatus === "error" ? "linear-gradient(135deg,#dc2626,#b91c1c)" : "linear-gradient(135deg,#1d4ed8,#2563eb)",
                            color: "#fff", opacity: (runTablesLoading || exportExcelLoading) ? 0.65 : 1, transition: "background 0.25s, opacity 0.2s",
                            position: "relative", overflow: "hidden",
                          }}
                        >
                          {runTablesLoading && runTablesProgress >= 0 && (
                            <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${runTablesProgress}%`, background: "rgba(255,255,255,0.15)", transition: "width 0.4s ease", pointerEvents: "none" }} />
                          )}
                          {runTablesLoading ? (
                            <><FontAwesomeIcon icon={faSpinner} spin />{runTablesProgress >= 0 ? `Calculating… ${runTablesProgress}%` : "Calculating…"}</>
                          ) : runTablesStatus === "done" ? (
                            <><FontAwesomeIcon icon={faCheckCircle} />Saved to Cache!</>
                          ) : runTablesStatus === "error" ? (
                            <><FontAwesomeIcon icon={faTimes} />Failed — Retry</>
                          ) : (
                            <><FontAwesomeIcon icon={faFileExcel} />Run Tables</>
                          )}
                        </button>
                        {runTablesLoading && runTablesMode === "spss_fallback" && (
                          <span
                            title="This project's data isn't loaded into the fast table cache yet, so it's running through the slower SPSS-file path."
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px",
                              borderRadius: 6, fontSize: 11, fontWeight: 600,
                              background: "#fef3c7", color: "#92400e",
                            }}
                          >
                            Slow mode (SPSS)
                          </span>
                        )}
                        {(() => {
                          const hasData = freqdataCount > 0;
                          const isLoading = freqdataCount === -1;
                          const disabled = exportExcelLoading || runTablesLoading || !hasData;
                          const bg = exportExcelStatus === "done" ? "linear-gradient(135deg,#16a34a,#15803d)" : exportExcelStatus === "error" ? "linear-gradient(135deg,#dc2626,#b91c1c)" : hasData ? "linear-gradient(135deg,#0f766e,#0d9488)" : "#1e2533";
                          const title = isLoading ? "Checking for cached tables…" : hasData ? `Export ${freqdataCount} cached table${freqdataCount !== 1 ? "s" : ""} to Excel` : "Run Tables first to generate cached data";
                          return (
                            <button
                              onClick={handleExportExcel}
                              disabled={disabled}
                              title={title}
                              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "9px 14px", borderRadius: 8, border: hasData ? "none" : "1px dashed #334155", cursor: disabled ? "not-allowed" : "pointer", fontWeight: 600, fontSize: 13, background: bg, color: hasData ? "#fff" : "#475569", opacity: disabled ? 0.6 : 1, transition: "background 0.25s, opacity 0.2s" }}
                            >
                              {exportExcelLoading ? (
                                <><FontAwesomeIcon icon={faSpinner} spin />Exporting…</>
                              ) : exportExcelStatus === "done" ? (
                                <><FontAwesomeIcon icon={faCheckCircle} />Downloaded!</>
                              ) : exportExcelStatus === "error" ? (
                                <><FontAwesomeIcon icon={faTimes} />Failed — Retry</>
                              ) : (
                                <><FontAwesomeIcon icon={faDownload} />Export Excel{hasData && <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.8 }}>({freqdataCount})</span>}</>
                              )}
                            </button>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        )}

        {!isPropertiesShow ? (
          <div className="data-tables-container">
            <div className="page-header">
              <h2>Frequency Data: {selectedTitle}</h2>
              <div className="header-actions">
                <button className={`btn btn-toggle ${isControlPanelOpen ? "active" : ""}`} onClick={() => setIsControlPanelOpen(!isControlPanelOpen)} title={isControlPanelOpen ? "Hide Control Panel" : "Show Control Panel"}>
                  <FontAwesomeIcon icon={faGear} />
                </button>
                <button className="btn btn-secondary" onClick={() => handleShowProperties()} title="Global Settings">
                  <FontAwesomeIcon icon={faGlobe} /><span>Global</span>
                </button>
              </div>
            </div>
            <div className="data-display-area">
              <>
                <div className="data-content">
                  {loading ? (
                    /* Spinner shown inline so it's always visible regardless of scroll position */
                    <div style={{
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      minHeight: 400, gap: 14,
                    }}>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 40, color: '#123abc' }} />
                      <p style={{ color: '#6b7280', margin: 0, fontSize: 14 }}>Loading…</p>
                    </div>
                  ) : (
                    <>
                    
                      {!isCrossTab && frequencyData && (frequencyData.type === "Custom" || frequencyData.type === "SingleResponse" || frequencyData.type === "Numeric") && displayMode === "table" && (
                        <FrequencyTableComponent freq_table={frequencyData} displaySettings={fullDisplaySettings} />
                      )}
                      {!isCrossTab && frequencyData && frequencyData.type === "Multiple2DGrid" && displayMode === "table" && (
                        <MultiResponse varProperties={frequencyData} displaySettings={fullDisplaySettings} />
                      )}
                      {!isCrossTab && frequencyData && (frequencyData.type === "Single2DGrid" || frequencyData.type === "Numeric2DGrid") && displayMode === "table" && (
                        <DataGrid gridData={frequencyData} displaySettings={fullDisplaySettings} grid={selectedVariable.grid} reverseGrid={selectedVariable.reverseGrid} />
                      )}
                      {!isCrossTab && frequencyData && (frequencyData.type === "Multiple3DGrid") && displayMode === "table" && (
                        <MultiDataGrid gridData={frequencyData} displaySettings={fullDisplaySettings} />
                      )}
                      {!isCrossTab && frequencyData && (frequencyData.type === "Numeric3DGrid" || frequencyData.type === "Single3DGrid") && displayMode === "table" && (
                        <Grid3DData gridData={frequencyData} displaySettings={fullDisplaySettings} />
                      )}
                      {displayMode === "chart" && frequencyData && Object.entries(frequencyData).map(([varName, freq], index) => (
                        <div key={index}><h3>{varName}</h3><MyChartComponent frequencyData={freq} /></div>
                      ))}
                      {isCrossTab && crosstabDisplayData && <CrosstabTable crosstab={crosstabDisplayData} />}
                    </>
                  )}
                </div>
              </>
            </div>
            <OpenProperties isOpen={isModalOpen} onClose={closeModalAndUpdateButtons} setdataGroup={setdataGroup} dataGroup={dataGroup} selectedKey={selectedTitle} selectedVariable={selectedVariable} setSelectedVariable={setSelectedVariable} projectId={projectId} />
          </div>
        ) : (
          <div className="properties-view">
            <div className="page-header" style={{ marginBottom: "24px" }}>
              <h2>Global Settings</h2>
              <div className="header-actions">
                <button className={`btn btn-toggle ${isControlPanelOpen ? "active" : ""}`} onClick={() => setIsControlPanelOpen(!isControlPanelOpen)} title={isControlPanelOpen ? "Hide Control Panel" : "Show Control Panel"}>
                  <FontAwesomeIcon icon={faGear} />
                </button>
              </div>
            </div>
            <button className="btn btn-back" onClick={() => handleShowProperties()} style={{ marginBottom: "24px" }}>
              <FontAwesomeIcon icon={faArrowLeft} /><span>Back to Tables</span>
            </button>
            <PropertiesComponent setdataGroup={setdataGroup} selectedVariable={selectedVariable} selectedKey={selectedTitle} dataGroup={dataGroup} setSelectedVariable={setSelectedVariable} projectId={projectId} nets={nets} setNets={setNets} />
          </div>
        )}
      </div>

      {/* ── AI Tab Specs Modal ─────────────────────────────────────────────── */}
      {isTabSpecsModalOpen && (
        <div
          className="modal-overlay"
          onClick={status === "success" ? undefined : toggleTabSpecsModal}
          style={status === "success" ? { alignItems: "stretch", padding: 0 } : {}}
        >
          <div
            className="modal-content"
            onClick={e => e.stopPropagation()}
            style={status === "success" ? {
              width: "100vw", maxWidth: "100vw", height: "100vh", maxHeight: "100vh",
              borderRadius: 0, margin: 0, display: "flex", flexDirection: "column", overflow: "hidden"
            } : {}}
          >
            <style>{`
              /* ── Base wrapper ───────────────────────────────────── */
              .ts-wrapper {
                font-family: 'Segoe UI', system-ui, sans-serif;
                background: #0f1117;
                color: #e2e8f0;
                border-radius: 16px;
                width: 580px;
                max-width: 95vw;
                max-height: 92vh;
                overflow-y: auto;
                box-shadow: 0 24px 64px rgba(0,0,0,0.6);
                display: flex;
                flex-direction: column;
              }
              /* ── Full-screen wrapper (post-success) ─────────────── */
              .ts-wrapper--fullscreen {
                width: 100%;
                height: 100%;
                max-width: 100%;
                max-height: 100%;
                border-radius: 0;
                overflow: hidden;
                display: flex;
                flex-direction: column;
              }
              .ts-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 14px 20px;
                border-bottom: 1px solid #1e2533;
                flex-shrink: 0;
                background: #0c0f16;
              }
              .ts-header h2 {
                margin: 0;
                font-size: 15px;
                font-weight: 600;
                color: #f1f5f9;
                display: flex;
                align-items: center;
                gap: 8px;
              }
              .ts-header h2 svg { color: #6366f1; }
              .ts-header-actions { display: flex; align-items: center; gap: 8px; }
              .ts-close-btn {
                background: none;
                border: none;
                color: #64748b;
                cursor: pointer;
                font-size: 16px;
                padding: 4px 8px;
                border-radius: 6px;
                transition: color 0.2s, background 0.2s;
              }
              .ts-close-btn:hover { color: #f1f5f9; background: #1e2533; }

              /* ── Upload body ────────────────────────────────────── */
              .ts-body { padding: 20px 24px; display: flex; flex-direction: column; gap: 16px; flex-shrink: 0; }
              .ts-divider {
                display: flex; align-items: center; gap: 10px;
                font-size: 11px; color: #475569; text-transform: uppercase; letter-spacing: 0.08em;
              }
              .ts-divider::before, .ts-divider::after { content: ''; flex: 1; height: 1px; background: #1e2533; }
              .ts-file-zone {
                background: #131822; border: 1.5px dashed #2a3344; border-radius: 12px;
                padding: 14px 16px; transition: border-color 0.2s, background 0.2s;
              }
              .ts-file-zone:hover { border-color: var(--accent, #6366f1); }
              .ts-file-zone--filled {
                border-style: solid; border-color: var(--accent, #6366f1);
                background: color-mix(in srgb, var(--accent, #6366f1) 6%, #131822);
              }
              .ts-zone-header { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
              .ts-zone-icon { color: var(--accent, #6366f1); font-size: 14px; }
              .ts-zone-label { font-size: 13px; font-weight: 600; color: #cbd5e1; flex: 1; }
              .ts-zone-clear { background: none; border: none; color: #64748b; cursor: pointer; font-size: 12px; padding: 2px 6px; border-radius: 4px; }
              .ts-zone-clear:hover { color: #f87171; }
              .ts-drop-label { display: flex; flex-direction: column; align-items: center; gap: 4px; cursor: pointer; color: #475569; font-size: 13px; padding: 8px 0; }
              .ts-drop-label svg { font-size: 18px; margin-bottom: 2px; }
              .ts-drop-label small { font-size: 11px; color: #334155; }
              .ts-drop-label:hover { color: #94a3b8; }
              .ts-file-info { display: flex; align-items: center; gap: 10px; font-size: 13px; }
              .ts-check { color: var(--accent, #22c55e); font-size: 16px; }
              .ts-file-name { color: #e2e8f0; font-weight: 500; word-break: break-all; }
              .ts-file-size { color: #64748b; font-size: 11px; margin-top: 2px; }
              .ts-status { border-radius: 10px; padding: 12px 16px; font-size: 13px; display: flex; align-items: flex-start; gap: 10px; }
              .ts-status--processing { background: #1e2b4a; color: #93c5fd; }
              .ts-status--success { background: #14281e; color: #86efac; border: 1px solid #166534; }
              .ts-status--error { background: #2a1518; color: #fca5a5; border: 1px solid #7f1d1d; }
              .ts-status svg { margin-top: 1px; flex-shrink: 0; }
              .ts-spin { animation: ts-rotate 1s linear infinite; }
              @keyframes ts-rotate { to { transform: rotate(360deg); } }
              .ts-progress-track { background: #1e2533; border-radius: 999px; height: 4px; overflow: hidden; }
              .ts-progress-fill { height: 100%; background: linear-gradient(90deg, #6366f1, #8b5cf6); border-radius: 999px; transition: width 0.4s ease; }
              .ts-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 24px 20px; border-top: 1px solid #1e2533; flex-shrink: 0; }
              .ts-btn { display: flex; align-items: center; gap: 7px; padding: 9px 18px; border-radius: 8px; font-size: 13px; font-weight: 500; cursor: pointer; border: none; transition: opacity 0.2s, background 0.2s; }
              .ts-btn:disabled { opacity: 0.45; cursor: not-allowed; }
              .ts-btn--ghost { background: #1e2533; color: #94a3b8; }
              .ts-btn--ghost:hover:not(:disabled) { background: #263145; color: #e2e8f0; }
              .ts-btn--primary { background: linear-gradient(135deg, #6366f1, #8b5cf6); color: #fff; }
              .ts-btn--primary:hover:not(:disabled) { opacity: 0.88; }
              .ts-btn--sm { padding: 6px 12px; font-size: 12px; }

              /* ── Split-screen layout ────────────────────────────── */
              .ts-split-body {
                flex: 1;
                display: flex;
                flex-direction: row;
                overflow: hidden;
                min-height: 0;
              }
              .ts-pane {
                flex: 1;
                display: flex;
                flex-direction: column;
                overflow: hidden;
                border-right: 1px solid #1e2533;
                min-width: 0;
              }
              .ts-pane:last-child { border-right: none; }
              .ts-pane-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 8px 14px;
                background: #0c0f16;
                border-bottom: 1px solid #1e2533;
                flex-shrink: 0;
              }
              .ts-pane-title {
                font-size: 11px;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.08em;
                display: flex;
                align-items: center;
                gap: 6px;
              }
              .ts-pane-title--json { color: #6366f1; }
              .ts-pane-title--banner { color: #f59e0b; }
              .ts-pane-editor {
                flex: 1;
                overflow: auto;
                background: #080b10;
                min-height: 0;
              }
              .ts-pane-editor::-webkit-scrollbar { width: 5px; height: 5px; }
              .ts-pane-editor::-webkit-scrollbar-thumb { background: #2a3344; border-radius: 3px; }
              .ts-json-textarea {
                width: 100%;
                height: 100%;
                min-height: 100%;
                background: transparent;
                border: none;
                outline: none;
                color: #a5f3fc;
                font-family: 'Fira Code', 'Cascadia Code', 'Consolas', monospace;
                font-size: 12px;
                line-height: 1.6;
                padding: 14px;
                resize: none;
                box-sizing: border-box;
              }
              .ts-json-textarea::placeholder { color: #1e2d40; }
              .ts-json-err {
                font-size: 11px;
                color: #f87171;
                padding: 4px 14px 6px;
                background: #1a0a0a;
                border-top: 1px solid #3b1212;
                flex-shrink: 0;
              }

              /* ── Bottom chat area ───────────────────────────────── */
              .ts-bottom {
                display: flex;
                flex-direction: column;
                border-top: 2px solid #1e2533;
                flex-shrink: 0;
                background: #0c0f16;
                max-height: 38vh;
              }
              .ts-chat-header {
                padding: 8px 16px;
                font-size: 11px;
                font-weight: 700;
                color: #6366f1;
                text-transform: uppercase;
                letter-spacing: 0.07em;
                display: flex;
                align-items: center;
                gap: 7px;
                border-bottom: 1px solid #1e2533;
                flex-shrink: 0;
              }
              .ts-chat-body {
                overflow-y: auto;
                padding: 10px 16px;
                display: flex;
                flex-direction: column;
                gap: 10px;
                flex: 1;
                min-height: 60px;
                background: #080b10;
              }
              .ts-chat-body::-webkit-scrollbar { width: 4px; }
              .ts-chat-body::-webkit-scrollbar-thumb { background: #2a3344; border-radius: 2px; }
              .ts-msg { max-width: 88%; padding: 8px 12px; border-radius: 10px; font-size: 13px; line-height: 1.5; white-space: pre-wrap; word-break: break-word; }
              .ts-msg--user { align-self: flex-end; background: linear-gradient(135deg, #4f46e5, #7c3aed); color: #fff; border-bottom-right-radius: 3px; }
              .ts-msg--assistant { align-self: flex-start; background: #131822; color: #cbd5e1; border-bottom-left-radius: 3px; border: 1px solid #1e2533; }
              .ts-msg--streaming::after { content: '▋'; display: inline-block; margin-left: 2px; animation: ts-blink 0.8s steps(1) infinite; }
              @keyframes ts-blink { 0%,100% { opacity: 1; } 50% { opacity: 0; } }
              .ts-chat-empty { color: #334155; font-size: 12px; text-align: center; padding: 16px 0; line-height: 1.6; }
              .ts-chat-input-row { display: flex; align-items: flex-end; gap: 8px; padding: 8px 14px 10px; border-top: 1px solid #1e2533; flex-shrink: 0; }
              .ts-chat-input { flex: 1; background: #131822; border: 1.5px solid #2a3344; border-radius: 8px; color: #e2e8f0; font-size: 13px; padding: 8px 12px; outline: none; resize: none; font-family: inherit; line-height: 1.4; max-height: 80px; transition: border-color 0.2s; }
              .ts-chat-input:focus { border-color: #6366f1; }
              .ts-chat-input::placeholder { color: #475569; }
              .ts-chat-send { background: linear-gradient(135deg, #6366f1, #8b5cf6); border: none; border-radius: 8px; color: #fff; cursor: pointer; font-size: 14px; padding: 9px 13px; transition: opacity 0.2s; flex-shrink: 0; }
              .ts-chat-send:disabled { opacity: 0.4; cursor: not-allowed; }
              .ts-chat-send:hover:not(:disabled) { opacity: 0.85; }

              /* ── JSON validity badge ────────────────────────────── */
              .ts-badge {
                font-size: 10px; font-weight: 600; padding: 2px 7px; border-radius: 999px;
                letter-spacing: 0.04em; flex-shrink: 0;
              }
              .ts-badge--ok { background: #14532d; color: #86efac; }
              .ts-badge--err { background: #7f1d1d; color: #fca5a5; }

              /* ── Variable Selector ──────────────────────────────── */
              .ts-wrapper--wide { width: 680px; }
              .ts-vsel-summary {
                display: flex; flex-wrap: wrap; gap: 8px;
                padding: 14px 24px 0;
              }
              .ts-vsel-chip {
                display: flex; align-items: center; gap: 5px;
                padding: 5px 12px; border-radius: 999px;
                font-size: 12px; font-weight: 600;
              }
              .ts-vsel-chip--ai    { background: #14532d; color: #86efac; }
              .ts-vsel-chip--auto  { background: #1e2533; color: #64748b; }
              .ts-vsel-chip--user  { background: #431407; color: #fb923c; }
              .ts-vsel-controls {
                display: flex; align-items: center; gap: 8px;
                padding: 12px 24px 0; flex-wrap: wrap;
              }
              .ts-vsel-search {
                flex: 1; min-width: 160px; background: #131822; border: 1px solid #2a3344;
                border-radius: 8px; padding: 7px 12px; color: #e2e8f0; font-size: 13px; outline: none;
              }
              .ts-vsel-search::placeholder { color: #475569; }
              .ts-vsel-search:focus { border-color: #6366f1; }
              .ts-vsel-filter-row {
                display: flex; gap: 6px; padding: 8px 24px 0; flex-wrap: wrap;
              }
              .ts-vsel-filter-btn {
                padding: 4px 12px; border-radius: 6px; border: 1px solid #2a3344;
                font-size: 12px; font-weight: 500; cursor: pointer; background: transparent;
                color: #64748b; transition: all 0.15s;
              }
              .ts-vsel-filter-btn:hover { background: #1e2533; color: #e2e8f0; }
              .ts-vsel-filter-btn--active { background: #1e2b4a; border-color: #6366f1; color: #a5b4fc; }
              .ts-vsel-bulk { display: flex; gap: 6px; flex-shrink: 0; }
              .ts-vsel-bulk-btn {
                padding: 5px 10px; border-radius: 6px; border: 1px solid #2a3344;
                font-size: 11px; font-weight: 600; cursor: pointer; background: #131822;
                color: #94a3b8; transition: all 0.15s;
              }
              .ts-vsel-bulk-btn:hover { background: #1e2533; color: #e2e8f0; }
              .ts-vsel-list {
                flex: 1; overflow-y: auto; margin: 10px 24px; max-height: 360px;
                border: 1px solid #1e2533; border-radius: 10px; background: #0c0f16;
              }
              .ts-vsel-list::-webkit-scrollbar { width: 4px; }
              .ts-vsel-list::-webkit-scrollbar-thumb { background: #2a3344; border-radius: 2px; }
              .ts-vsel-row {
                display: flex; align-items: center; gap: 10px;
                padding: 8px 14px; border-bottom: 1px solid #1a2030;
                transition: background 0.12s; cursor: pointer;
              }
              .ts-vsel-row:last-child { border-bottom: none; }
              .ts-vsel-row:hover { background: #111827; }
              .ts-vsel-row--auto { opacity: 0.42; cursor: default; }
              .ts-vsel-row--auto:hover { background: transparent; }
              .ts-vsel-check {
                width: 16px; height: 16px; border-radius: 4px; border: 1.5px solid #334155;
                display: flex; align-items: center; justify-content: center;
                flex-shrink: 0; background: transparent; transition: all 0.15s;
              }
              .ts-vsel-check--on { background: #6366f1; border-color: #6366f1; }
              .ts-vsel-check--on svg { color: #fff; font-size: 9px; }
              .ts-vsel-key {
                font-family: 'Fira Code', monospace; font-size: 12px; font-weight: 700;
                color: #a5b4fc; min-width: 70px; flex-shrink: 0;
              }
              .ts-vsel-title {
                flex: 1; font-size: 12px; color: #94a3b8;
                white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
              }
              .ts-vsel-type {
                font-size: 10px; font-weight: 600; padding: 2px 7px; border-radius: 4px;
                background: #1e2533; color: #475569; flex-shrink: 0;
              }
              .ts-vsel-reason {
                font-size: 10px; color: #475569; flex-shrink: 0; font-style: italic;
              }
              .ts-vsel-empty {
                text-align: center; padding: 32px 0; color: #475569; font-size: 13px;
              }
            `}</style>

            {status !== "success" && tsStep === "upload" ? (
              /* ── STEP 1: UPLOAD FILES ── */
              <div className="ts-wrapper">
                <div className="ts-header">
                  <h2><FontAwesomeIcon icon={faRobot} /> AI Tab Specs Processor</h2>
                  <button className="ts-close-btn" onClick={toggleTabSpecsModal}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>

                <div className="ts-body">
                  <div className="ts-divider">Upload files</div>

                  {/* Tab Specification zone */}
                  <div
                    className={`ts-file-zone ${tabSpecFile ? "ts-file-zone--filled" : ""}`}
                    style={{ "--accent": "#10b981" }}
                    onDrop={makeDropHandler(setTabSpecFile)}
                    onDragOver={preventDefaults}
                    onDragEnter={preventDefaults}
                  >
                    <div className="ts-zone-header">
                      <FontAwesomeIcon icon={faFileExcel} className="ts-zone-icon" />
                      <span className="ts-zone-label">Tab Specification</span>
                      {tabSpecFile && (
                        <button className="ts-zone-clear" title="Remove file" onClick={() => { setTabSpecFile(null); if (tabSpecInputRef.current) tabSpecInputRef.current.value = ""; }}>
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                    {tabSpecFile ? (
                      <div className="ts-file-info">
                        <FontAwesomeIcon icon={faCheckCircle} className="ts-check" />
                        <div>
                          <div className="ts-file-name">{tabSpecFile.name}</div>
                          <div className="ts-file-size">{formatFileSize(tabSpecFile.size)}</div>
                        </div>
                      </div>
                    ) : (
                      <label className="ts-drop-label">
                        <FontAwesomeIcon icon={faUpload} />
                        <span>Drop file or click to browse</span>
                        <small>.xlsx / .xls / .csv — may be non-English</small>
                        <input ref={tabSpecInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleTabSpecChange} style={{ display: "none" }} />
                      </label>
                    )}
                  </div>

                  {/* Questionnaire zone */}
                  <div
                    className={`ts-file-zone ${questionnaireFile ? "ts-file-zone--filled" : ""}`}
                    style={{ "--accent": "#f59e0b" }}
                    onDrop={makeDropHandler(setQuestionnaireFile)}
                    onDragOver={preventDefaults}
                    onDragEnter={preventDefaults}
                  >
                    <div className="ts-zone-header">
                      <FontAwesomeIcon icon={faFileAlt} className="ts-zone-icon" />
                      <span className="ts-zone-label">Questionnaire <span style={{ fontSize: 10, fontWeight: 400, color: "#64748b", marginLeft: 4 }}>(optional)</span></span>
                      {questionnaireFile && (
                        <button className="ts-zone-clear" title="Remove file" onClick={() => { setQuestionnaireFile(null); if (questionnaireInputRef.current) questionnaireInputRef.current.value = ""; }}>
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                    {questionnaireFile ? (
                      <div className="ts-file-info">
                        <FontAwesomeIcon icon={faCheckCircle} className="ts-check" />
                        <div>
                          <div className="ts-file-name">{questionnaireFile.name}</div>
                          <div className="ts-file-size">{formatFileSize(questionnaireFile.size)}</div>
                        </div>
                      </div>
                    ) : (
                      <label className="ts-drop-label">
                        <FontAwesomeIcon icon={faUpload} />
                        <span>Drop file or click to browse</span>
                        <small>.xlsx / .xls / .docx — may be non-English</small>
                        <input ref={questionnaireInputRef} type="file" accept=".xlsx,.xls,.docx,.doc" onChange={handleQuestionnaireChange} style={{ display: "none" }} />
                      </label>
                    )}
                  </div>

                  {status === "processing" && (
                    <div className="ts-progress-track">
                      <div className="ts-progress-fill" style={{ width: `${progress}%` }} />
                    </div>
                  )}

                  {status !== "idle" && (
                    <div className={`ts-status ts-status--${status}`}>
                      <FontAwesomeIcon
                        icon={status === "processing" ? faSpinner : status === "success" ? faCheckCircle : faTimes}
                        className={status === "processing" ? "ts-spin" : ""}
                      />
                      <span>{statusMessage}</span>
                    </div>
                  )}
                </div>

                <div className="ts-footer">
                  <button className="ts-btn ts-btn--ghost" onClick={handleReset} disabled={status === "processing"}>Reset</button>
                  <button className="ts-btn ts-btn--ghost" onClick={toggleTabSpecsModal} disabled={status === "processing"}>Cancel</button>
                  {tabSpecFile && status === "idle" && (
                    <button
                      className="ts-btn ts-btn--ghost"
                      onClick={() => { setVarSearch(""); setVarFilter("all"); setTsStep("select"); }}
                      title="Preview and select variables before processing"
                    >
                      <FontAwesomeIcon icon={faList} /> Review Variables
                    </button>
                  )}
                  <button
                    className="ts-btn ts-btn--primary"
                    onClick={handleProcess}
                    disabled={!tabSpecFile || status === "processing" || status === "success"}
                  >
                    {status === "processing" ? (
                      <><FontAwesomeIcon icon={faSpinner} className="ts-spin" /> Processing…</>
                    ) : (
                      <><FontAwesomeIcon icon={faRobot} /> Process with AI</>
                    )}
                  </button>
                </div>
              </div>

            ) : status !== "success" && tsStep === "select" ? (() => {
              /* ── STEP 2: VARIABLE SELECTION ── */
              const allVars = Object.entries(dataGroup || {}).map(([key, value]) => ({
                key,
                value,
                status: getVarStatus(key, value),
                qtitle: (value?.Qtitle || "").trim(),
                type: (value?.type || ""),
              }));

              const aiCount = allVars.filter(v => v.status === "ai").length;
              const autoCount = allVars.filter(v => v.status === "auto").length;
              const userCount = allVars.filter(v => v.status === "user").length;

              const filtered = allVars.filter(({ key, qtitle, status: s }) => {
                if (varFilter === "ai" && s !== "ai") return false;
                if (varFilter === "excluded" && s === "ai") return false;
                if (varSearch) {
                  const q = varSearch.toLowerCase();
                  return key.toLowerCase().includes(q) || qtitle.toLowerCase().includes(q);
                }
                return true;
              });

              const eligibleFiltered = filtered.filter(v => v.status !== "auto");

              return (
                <div className="ts-wrapper ts-wrapper--wide">
                  <div className="ts-header">
                    <h2><FontAwesomeIcon icon={faRobot} /> Select Variables for AI</h2>
                    <button className="ts-close-btn" onClick={toggleTabSpecsModal}>
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>

                  {/* Summary badges */}
                  <div className="ts-vsel-summary">
                    <span className="ts-vsel-chip ts-vsel-chip--ai">
                      ✅ {aiCount} for AI
                    </span>
                    <span className="ts-vsel-chip ts-vsel-chip--auto">
                      ⊘ {autoCount} auto-excluded
                    </span>
                    {userCount > 0 && (
                      <span className="ts-vsel-chip ts-vsel-chip--user">
                        ❌ {userCount} excluded by you
                      </span>
                    )}
                  </div>

                  {/* Search + bulk actions */}
                  <div className="ts-vsel-controls">
                    <input
                      className="ts-vsel-search"
                      placeholder="Search by key or title…"
                      value={varSearch}
                      onChange={e => setVarSearch(e.target.value)}
                    />
                    <div className="ts-vsel-bulk">
                      <button
                        className="ts-vsel-bulk-btn"
                        title="Include all visible eligible variables"
                        onClick={() => setAllFilteredRemoveTab(eligibleFiltered, false)}
                      >Select All</button>
                      <button
                        className="ts-vsel-bulk-btn"
                        title="Exclude all visible eligible variables"
                        onClick={() => setAllFilteredRemoveTab(eligibleFiltered, true)}
                      >Deselect All</button>
                    </div>
                  </div>

                  {/* Filter tabs */}
                  <div className="ts-vsel-filter-row">
                    {[
                      { id: "all", label: `All (${allVars.length})` },
                      { id: "ai", label: `For AI (${aiCount})` },
                      { id: "excluded", label: `Excluded (${autoCount + userCount})` },
                    ].map(f => (
                      <button
                        key={f.id}
                        className={`ts-vsel-filter-btn ${varFilter === f.id ? "ts-vsel-filter-btn--active" : ""}`}
                        onClick={() => setVarFilter(f.id)}
                      >{f.label}</button>
                    ))}
                  </div>

                  {/* Variable list */}
                  <div className="ts-vsel-list">
                    {filtered.length === 0 ? (
                      <div className="ts-vsel-empty">No variables match your filter.</div>
                    ) : filtered.map(({ key, value, status: s, qtitle, type }) => {
                      const isAuto = s === "auto";
                      const isOn = s === "ai";
                      return (
                        <div
                          key={key}
                          className={`ts-vsel-row ${isAuto ? "ts-vsel-row--auto" : ""}`}
                          onClick={isAuto ? undefined : () => toggleVarRemoveTab(key)}
                          title={isAuto ? `Auto-excluded: ${getAutoReason(key, value)}` : isOn ? "Click to exclude from AI" : "Click to include in AI"}
                        >
                          {/* Checkbox */}
                          <div className={`ts-vsel-check ${isOn ? "ts-vsel-check--on" : ""}`}>
                            {isOn && <FontAwesomeIcon icon={faCheck} />}
                          </div>

                          {/* Key */}
                          <span className="ts-vsel-key">{key}</span>

                          {/* Title */}
                          <span className="ts-vsel-title" title={qtitle}>
                            {qtitle || <em style={{ color: "#334155" }}>no title</em>}
                          </span>

                          {/* Type */}
                          {type && <span className="ts-vsel-type">{type}</span>}

                          {/* Auto-exclude reason */}
                          {isAuto && (
                            <span className="ts-vsel-reason">{getAutoReason(key, value)}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="ts-footer">
                    <button className="ts-btn ts-btn--ghost" onClick={() => setTsStep("upload")}>
                      ← Back
                    </button>
                    <button
                      className="ts-btn ts-btn--primary"
                      onClick={() => { setTsStep("upload"); handleProcess(); }}
                      disabled={aiCount === 0}
                      title={aiCount === 0 ? "No variables selected for AI" : `Process ${aiCount} variables with AI`}
                    >
                      <FontAwesomeIcon icon={faRobot} /> Process {aiCount} var{aiCount !== 1 ? "s" : ""} with AI
                    </button>
                  </div>
                </div>
              );
            })() : (
              /* ── SUCCESS PHASE: full-screen split-screen editor ── */
              <div style={{
                position: "fixed",
                top: 0, left: 0, right: 0, bottom: 0,
                zIndex: 99999,
                background: "#0f1117",
                height: "100vh",
                overflow: "hidden",
              }}><SplitScreenEditor
                  dataGroup={dataGroup}
                  setdataGroup={setdataGroup}
                  banners={banners}
                  setBanners={setBanners}
                  chatMessages={chatMessages}
                  chatInput={chatInput}
                  setChatInput={setChatInput}
                  chatBusy={chatBusy}
                  chatBodyRef={chatBodyRef}
                  chatInputRef2={chatInputRef2}
                  handleChatSend={handleChatSend}
                  handleReset={handleReset}
                  toggleTabSpecsModal={toggleTabSpecsModal}
                  handleSaveToDb={handleSaveToDb}
                  saveStatus={saveStatus}
                  saveError={saveError}
                /></div>
            )}
          </div>
        </div>
      )}

      <CustomizedVariable isOpen={isAModalOpen} onClose={acloseModalAndUpdateButtons} setcurrentSelectedVariablesset={setcurrentSelectedVariablesset} dataGroup={dataGroup} projectId={projectId} />
      <DeleteIdsModal isOpen={isDeleteIdsOpen} onClose={() => setIsDeleteIdsOpen(false)} projectId={projectId} user={user?.user?.email || user?.email || powner} />
      <OeImportModal
        isOpen={isOeImportOpen}
        onClose={() => setIsOeImportOpen(false)}
        projectId={projectId}
        user={user?.user?.email || user?.email || powner}
        client={selectedClient}
        project={selectedProject}
        onImported={(newQgroup) => {
          setdataGroup(newQgroup);
          if (selectedTitle) handleTitleClick(selectedTitle);
        }}
      />
      <ExcelVariableUploadModal
        isOpen={isExcelVarUploadOpen}
        onClose={() => setIsExcelVarUploadOpen(false)}
        projectId={projectId}
        user={user?.user?.email || user?.email || powner}
      />
      <MoveVariableToGridModal
        isOpen={isMoveVarOpen}
        onClose={() => setIsMoveVarOpen(false)}
        projectId={projectId}
        user={user?.user?.email || user?.email || powner}
      />

      {/* ── Export full-screen overlay spinner ── */}
      {exportOverlay && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 99998,
          background: "rgba(10, 15, 30, 0.78)",
          backdropFilter: "blur(3px)",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          gap: 24,
        }}>
          {/* Ring spinner */}
          <div style={{
            width: 80, height: 80,
            borderRadius: "50%",
            border: "6px solid rgba(255,255,255,0.1)",
            borderTopColor: exportOverlay === "Quantum" ? "#a78bfa"
              : exportOverlay === "Uncle" ? "#34d399"
                : exportOverlay === "Wincross" ? "#60a5fa"
                  : "#f59e0b",
            animation: "dt-spin 0.9s linear infinite",
          }} />
          <div style={{ textAlign: "center", color: "#f1f5f9" }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>
              Generating {exportOverlay} files…
            </div>
            <div style={{ fontSize: 13, color: "#94a3b8" }}>
              Please wait, this may take a moment
            </div>
          </div>
          <style>{`@keyframes dt-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      <RunTableSettingsModal
        open={settingsModalOpen}
        settings={tableSettings}
        onChange={setTableSettings}
        onClose={() => setSettingsModalOpen(false)}
        hasWeights={!!selectedWeights}
        outputSets={outputSets}
        onChangeOutputSets={handleChangeOutputSets}
      />
      <RunMatrixModal
        open={runMatrixModalOpen}
        onClose={() => setRunMatrixModalOpen(false)}
        banners={banners}
        savedWeightings={savedWeightings}
        outputSets={outputSets}
        runs={runMatrixRows}
        onChangeRuns={handleChangeRunMatrixRows}
        onRunAll={handleRunMatrixAll}
        onDownloadAll={handleDownloadRunMatrixAll}
        batchStatus={runMatrixStatus}
        batchProgress={runMatrixProgress}
        batchRows={runMatrixBatchRows}
        bilendiEnabled={isbilendi}
      />
      <DataGroupVersionsModal
        isOpen={dataGroupVersionsModalOpen}
        onClose={() => setDataGroupVersionsModalOpen(false)}
        versions={dataGroupVersions}
        loading={dataGroupVersionsLoading}
        busyId={dataGroupVersionBusyId}
        onRestore={handleRestoreDataGroupVersion}
      />
    </>
  );
}

// ── Split-screen editor (full-screen, rendered after AI processing) ────────────
function SplitScreenEditor({
  dataGroup, setdataGroup,
  banners, setBanners,
  chatMessages, chatInput, setChatInput,
  chatBusy, chatBodyRef, chatInputRef2,
  handleChatSend, handleReset, toggleTabSpecsModal,
  handleSaveToDb, saveStatus, saveError,
}) {
  // Local editable JSON strings for the two panes
  const [jsonStr, setJsonStr] = useState(() => JSON.stringify(dataGroup, null, 2));
  const [bannerStr, setBannerStr] = useState(() => JSON.stringify(banners, null, 2));
  const [jsonErr, setJsonErr] = useState(null);
  const [bannerErr, setBannerErr] = useState(null);

  // Keep pane content in sync when AI updates the context values
  useEffect(() => { setJsonStr(JSON.stringify(dataGroup, null, 2)); setJsonErr(null); }, [dataGroup]);
  useEffect(() => { setBannerStr(JSON.stringify(banners, null, 2)); setBannerErr(null); }, [banners]);

  const handleJsonChange = (val) => {
    setJsonStr(val);
    try { setdataGroup(JSON.parse(val)); setJsonErr(null); }
    catch (e) { setJsonErr(e.message); }
  };

  const handleBannerChange = (val) => {
    setBannerStr(val);
    try { const p = JSON.parse(val); if (Array.isArray(p)) { setBanners(p); setBannerErr(null); } else setBannerErr("Must be a JSON array"); }
    catch (e) { setBannerErr(e.message); }
  };

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", width: "100%", height: "100vh", background: "#0f1117", color: "#e2e8f0", fontFamily: "'Segoe UI', system-ui, sans-serif", overflow: "hidden" }}>

        {/* ── Top bar ── */}
        <div style={{ display: "flex", marginTop: "5%", alignItems: "center", justifyContent: "space-between", padding: "10px 18px", borderBottom: "1px solid #1e2533", background: "#0c0f16", flexShrink: 0 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 14, color: "#f1f5f9" }}>
            <FontAwesomeIcon icon={faRobot} style={{ color: "#6366f1" }} />
            AI Tab Specs — Review &amp; Edit
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            {/* ── Save to Database button ── */}
            <button
              className="ts-btn ts-btn--sm"
              onClick={handleSaveToDb}
              disabled={saveStatus === "saving"}
              title={saveError || "Save current JSON & Banners to the database"}
              style={{
                background:
                  saveStatus === "saved" ? "#16a34a" :
                    saveStatus === "error" ? "#dc2626" :
                      saveStatus === "saving" ? "#1e293b" :
                        "#4f46e5",
                color: "#fff",
                border: "none",
                cursor: saveStatus === "saving" ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", gap: 6,
                padding: "5px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600,
                transition: "background 0.2s",
                minWidth: 120,
              }}
            >
              {saveStatus === "saving" ? (
                <><FontAwesomeIcon icon={faSpinner} className="ts-spin" /> Saving…</>
              ) : saveStatus === "saved" ? (
                <><FontAwesomeIcon icon={faCheckCircle} /> Saved!</>
              ) : saveStatus === "error" ? (
                <><FontAwesomeIcon icon={faTimes} /> Save Failed</>
              ) : (
                <><FontAwesomeIcon icon={faSave} /> Save to DB</>
              )}
            </button>
            {saveError && saveStatus === "error" && (
              <span style={{ fontSize: 11, color: "#f87171", alignSelf: "center", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {saveError}
              </span>
            )}
            <button className="ts-btn ts-btn--ghost ts-btn--sm" onClick={handleReset}>
              <FontAwesomeIcon icon={faRefresh} /> Reset
            </button>
            <button className="ts-btn ts-btn--ghost ts-btn--sm" onClick={toggleTabSpecsModal}>
              <FontAwesomeIcon icon={faTimes} /> Close
            </button>
          </div>
        </div>

        {/* ── Split panes ── */}
        <div style={{ flex: 1, display: "flex", flexDirection: "row", overflow: "hidden", minHeight: 0 }}>

          {/* Left pane — DataGroup JSON */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", borderRight: "1px solid #1e2533", minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 14px", background: "#0c0f16", borderBottom: "1px solid #1e2533", flexShrink: 0 }}>
              <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#6366f1", display: "flex", alignItems: "center", gap: 6 }}>
                <FontAwesomeIcon icon={faTable} /> DataGroup JSON
              </span>
              <span className={`ts-badge ${jsonErr ? "ts-badge--err" : "ts-badge--ok"}`}>
                {jsonErr ? "Invalid JSON" : "Valid"}
              </span>
            </div>
            <div style={{ flex: 1, position: "relative", background: "#080b10", minHeight: 0 }}>
              <textarea
                style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, width: "100%", height: "100%", background: "transparent", border: "none", outline: "none", color: "#a5f3fc", fontFamily: "'Fira Code','Cascadia Code','Consolas',monospace", fontSize: 12, lineHeight: 1.6, padding: 14, resize: "none", boxSizing: "border-box", overflow: "auto" }}
                value={jsonStr}
                onChange={e => handleJsonChange(e.target.value)}
                spellCheck={false}
              />
            </div>
            {jsonErr && (
              <div style={{ fontSize: 11, color: "#f87171", padding: "4px 14px 6px", background: "#1a0a0a", borderTop: "1px solid #3b1212", flexShrink: 0 }}>
                ⚠ {jsonErr}
              </div>
            )}
          </div>

          {/* Right pane — Banners JSON */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 14px", background: "#0c0f16", borderBottom: "1px solid #1e2533", flexShrink: 0 }}>
              <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#f59e0b", display: "flex", alignItems: "center", gap: 6 }}>
                <FontAwesomeIcon icon={faArrowsLeftRight} /> Banners JSON
              </span>
              <span className={`ts-badge ${bannerErr ? "ts-badge--err" : "ts-badge--ok"}`}>
                {bannerErr ? "Invalid JSON" : "Valid"}
              </span>
            </div>
            <div style={{ flex: 1, position: "relative", background: "#080b10", minHeight: 0 }}>
              <textarea
                style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, width: "100%", height: "100%", background: "transparent", border: "none", outline: "none", color: "#fde68a", fontFamily: "'Fira Code','Cascadia Code','Consolas',monospace", fontSize: 12, lineHeight: 1.6, padding: 14, resize: "none", boxSizing: "border-box", overflow: "auto" }}
                value={bannerStr}
                onChange={e => handleBannerChange(e.target.value)}
                spellCheck={false}
              />
            </div>
            {bannerErr && (
              <div style={{ fontSize: 11, color: "#f87171", padding: "4px 14px 6px", background: "#1a0a0a", borderTop: "1px solid #3b1212", flexShrink: 0 }}>
                ⚠ {bannerErr}
              </div>
            )}
          </div>
        </div>

        {/* ── Bottom chat strip ── */}
        <div style={{ display: "flex", flexDirection: "column", borderTop: "2px solid #1e2533", flexShrink: 0, background: "#0c0f16", maxHeight: "36vh" }}>
          <div style={{ padding: "7px 16px", fontSize: 11, fontWeight: 700, color: "#6366f1", textTransform: "uppercase", letterSpacing: "0.07em", display: "flex", alignItems: "center", gap: 7, borderBottom: "1px solid #1e2533", flexShrink: 0 }}>
            <FontAwesomeIcon icon={faComments} /> Real-time AI Editing
            <span style={{ marginLeft: "auto", fontSize: 10, color: "#475569", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
              AI edits update both panes live
            </span>
          </div>

          <div className="ts-chat-body" ref={chatBodyRef} style={{ flex: 1, minHeight: 50, maxHeight: "none" }}>
            {chatMessages.length === 0 ? (
              <div className="ts-chat-empty">
                Ask the AI to make further edits — changes will reflect in the panes above instantly.<br />
                e.g. <em>"Change Q1 basetitle to All adults"</em> or <em>"Add a top 2 box net to Q3"</em>
              </div>
            ) : (
              chatMessages.map((msg, i) => (
                <div key={i} className={`ts-msg ts-msg--${msg.role}${msg.isStreaming ? " ts-msg--streaming" : ""}`}>
                  {msg.text}
                </div>
              ))
            )}
          </div>

          <div className="ts-chat-input-row">
            <textarea
              ref={chatInputRef2}
              className="ts-chat-input"
              rows={1}
              placeholder="Ask AI to edit the JSON… (Enter to send, Shift+Enter for newline)"
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleChatSend(); } }}
              disabled={chatBusy}
            />
            <button className="ts-chat-send" onClick={handleChatSend} disabled={chatBusy || !chatInput.trim()} title="Send message">
              {chatBusy ? <FontAwesomeIcon icon={faSpinner} className="ts-spin" /> : <FontAwesomeIcon icon={faPaperPlane} />}
            </button>
          </div>
        </div>
      </div>

    </>
  );
}

export default DataTables;